import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import * as XLSX from 'xlsx';
import {
  departmentsApi,
  documentsApi,
  getAuthHeaders,
  listApi,
  peopleApi,
  raciApi,
  riskApi,
} from '../services/api';
import { API_BASE_URL } from '../config';
import { FLOW_COLUMNS, loadFlowChecks } from '../data/ismsFlowData';
import type { FlowColumn } from '../data/ismsFlowData';
import { NimbusTable } from '../components/NimbusTable';
import { maturityToScore, isImplementationComplete, parseEffectivenessScore } from '../utils/controlConformity';
import { parseRiskScore, categoryFromScore } from '../utils/riskScoring';
import { exportReportsPdf } from '../utils/reportsPdf';

type Risk = {
  // Lifecycle status ('open'/'in_progress'/'mitigated'/'accepted', lowercase - see
  // prod-nimbus-risks update_risk) is its own top-level DynamoDB attribute, NOT a generic
  // catalog field - it never lived in `fields` (see computeTreatmentPipeline below).
  status?: string;
  fields?: Record<string, any>;
};

type Control = {
  controlId?: string;
  id?: string;
  type?: string;
  controlType?: string;
  frameworkId?: string;
  framework?: string;
  matrixId?: string;
  fields?: Record<string, any>;
};

type Issue = { fields?: Record<string, any> };

type Incident = { fields?: Record<string, any> };

type Department = {
  departmentId?: string;
  id?: string;
  department_name?: string;
  name?: string;
};

type RaciAssignment = {
  controlId?: string;
  artefactId?: string;  // RACI API may use either field
  personId?: string;
  role?: string; // 'A' | 'R' | ...
};

type Person = {
  personId?: string;
  id?: string;
  departmentId?: string;
  department?: string;
  departmentName?: string;
};

type ReportsData = {
  risks: Risk[];
  controls: Control[];
  departments: Department[];
  issues: Issue[];
  incidents: Incident[];
  raciAssignments: RaciAssignment[];
  people: Person[];
  documents: any[];
  evidenceItems: any[];
};

type RiskStats = {
  total: number;
  critical: number;
  // Same >=15 threshold as `critical` (residual-based) above - the inherent-side equivalent,
  // for a PDF export section that shows the register from the inherent perspective on its own
  // rather than only ever alongside its residual counterpart.
  criticalInherent: number;
  avgInherentRisk: string | number;
  avgResidualRisk: string | number;
  mitigated: number;
};

const truncate = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) return value;
  return value.substring(0, maxLength - 3) + '...';
};

const mapLikelihoodToNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  const map: Record<string, number> = {
    rare: 1,
    unlikely: 2,
    possible: 3,
    likely: 4,
    'almost certain': 5,
    'very low': 1,
    low: 2,
    medium: 3,
    high: 4,
    'very high': 5,
  };
  return map[String(value ?? '').toLowerCase()] || 1;
};

const mapImpactToNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  const map: Record<string, number> = {
    insignificant: 1,
    minor: 2,
    moderate: 3,
    major: 4,
    catastrophic: 5,
    'very low': 1,
    low: 2,
    medium: 3,
    high: 4,
    'very high': 5,
  };
  return map[String(value ?? '').toLowerCase()] || 1;
};

const computeRiskHeatmapSeries = (risks: Risk[]) => {
  const matrix: number[][] = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => 0));

  risks.forEach((risk) => {
    const fields = risk.fields || {};

    let likelihood = mapLikelihoodToNumber(fields.inherentLikelihood || fields.likelihood || fields.residualLikelihood || 1);
    let impact = mapImpactToNumber(fields.inherentImpact || fields.impact || fields.residualImpact || 1);

    if (Number.isNaN(likelihood) || likelihood < 1 || likelihood > 5) likelihood = 1;
    if (Number.isNaN(impact) || impact < 1 || impact > 5) impact = 1;

    const lIdx = Math.max(0, Math.min(4, likelihood - 1));
    const iIdx = Math.max(0, Math.min(4, impact - 1));
    matrix[lIdx][iIdx] += 1;
  });

  return matrix.map((row, idx) => ({
    name: `Likelihood ${idx + 1}`,
    data: row,
  }));
};

/** Residual counterpart of the heat map above, for the PDF export's "Residual Risk" section -
 *  there's no separate stored residual likelihood/impact to plot on the same two axes (only the
 *  combined residual_risk score exists), so instead of fabricating axis values this shows the
 *  real thing that IS available: how many risks land in each real risk band (same 21/16/11/6
 *  thresholds as everywhere else - categoryFromScore, riskScoring.ts) once controls are
 *  accounted for. */
const computeResidualBandDistribution = (risks: Risk[]) => {
  const BANDS = ['Minimal', 'Low', 'Medium', 'High', 'Critical'];
  const counts: Record<string, number> = { Minimal: 0, Low: 0, Medium: 0, High: 0, Critical: 0 };

  risks.forEach((risk) => {
    const fields = risk.fields || {};
    const likelihood = mapLikelihoodToNumber(fields.likelihood);
    const impact = mapImpactToNumber(fields.impact);
    const score = parseRiskScore(fields.residual_risk) ?? likelihood * impact;
    const band = categoryFromScore(score) || 'Minimal';
    counts[band] = (counts[band] ?? 0) + 1;
  });

  return { labels: BANDS, values: BANDS.map((b) => counts[b]) };
};

const computeTopRisks = (risks: Risk[]) => {
  const scored = risks
    .map((risk) => {
      const fields = risk.fields || {};
      // See computeRiskStats above - residualLikelihood/residualImpact are never actually
      // populated, so prefer the real persisted residual_risk score and only fall back to a
      // crude likelihood x impact estimate (effectively inherent) when it hasn't been computed
      // at all yet.
      const likelihood = mapLikelihoodToNumber(fields.residualLikelihood || fields.likelihood || fields.inherentLikelihood || 1);
      const impact = mapImpactToNumber(fields.residualImpact || fields.impact || fields.inherentImpact || 1);
      const score = parseRiskScore(fields.residual_risk) ?? likelihood * impact;
      return {
        title: String(fields.title || fields.risk_title || 'Untitled Risk'),
        score,
      };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);

  if (scored.length === 0) {
    return { labels: ['No risks found'], scores: [0] };
  }

  return {
    labels: scored.map((r) => truncate(r.title, 30)),
    scores: scored.map((r) => r.score),
  };
};

const computeTreatmentPipeline = (risks: Risk[]) => {
  const statusCounts: Record<string, number> = {
    Open: 0,
    'In Progress': 0,
    Mitigated: 0,
    Accepted: 0,
  };

  // Real status values are lowercase, underscored ('open', 'in_progress', ...) - never inside
  // `fields`, always this top-level attribute (see the Risk type above). Reading `fields?.status`
  // here meant every risk fell through to the 'Open' default regardless of its real status.
  const humanize = (raw: string): string =>
    raw.trim().toLowerCase().split(/[_\s]+/).filter(Boolean)
      .map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');

  risks.forEach((risk) => {
    const status = humanize(String(risk.status || 'open'));
    if (Object.prototype.hasOwnProperty.call(statusCounts, status)) {
      statusCounts[status] += 1;
    } else {
      statusCounts.Open += 1;
    }
  });

  return {
    labels: Object.keys(statusCounts),
    values: Object.values(statusCounts),
  };
};

const computeRiskByCategory = (risks: Risk[]) => {
  const counts: Record<string, number> = {};
  risks.forEach((risk) => {
    const fields = risk.fields || {};
    const category = String(
      fields.category || fields.risk_category || fields.riskCategory || fields.type || fields.risk_type || fields.business_process || 'Uncategorized',
    );
    counts[category] = (counts[category] || 0) + 1;
  });

  const sorted = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 7);

  return {
    labels: sorted.map(([label]) => label),
    values: sorted.map(([, value]) => value),
  };
};

const computeRiskScoreByCategory = (risks: Risk[]) => {
  const categoryScores: Record<string, number> = {};
  const categoryCounts: Record<string, number> = {};

  risks.forEach((risk) => {
    const fields = risk.fields || {};
    const category = String(
      fields.category || fields.risk_category || fields.riskCategory || fields.type || fields.risk_type || fields.business_process || 'Uncategorized',
    );

    const likelihood = mapLikelihoodToNumber(fields.likelihood);
    const impact = mapImpactToNumber(fields.impact);
    const score = likelihood * impact;

    if (!categoryScores[category]) {
      categoryScores[category] = 0;
      categoryCounts[category] = 0;
    }

    categoryScores[category] += score;
    categoryCounts[category] += 1;
  });

  const sorted = Object.entries(categoryScores)
    .map(([category, totalScore]) => ({
      category,
      totalScore,
      count: categoryCounts[category] || 0,
      avgScore: (categoryCounts[category] || 1) > 0 ? totalScore / (categoryCounts[category] || 1) : 0,
    }))
    .sort((a, b) => b.totalScore - a.totalScore)
    .slice(0, 10);

  return {
    labels: sorted.map((item) => item.category),
    values: sorted.map((item) => item.totalScore),
    counts: sorted.map((item) => item.count),
    avgScores: sorted.map((item) => item.avgScore),
  };
};

const computeRiskStats = (risks: Risk[]): RiskStats => {
  const total = risks.length;
  let critical = 0;
  let criticalInherent = 0;
  let totalInherentRisk = 0;
  let totalResidualRisk = 0;
  let mitigated = 0;

  risks.forEach((risk) => {
    const fields = risk.fields || {};

    // Prefer the real, control-aware inherent_risk/residual_risk scores RiskDetail.tsx computes
    // and persists ("High (16)", parsed back to 16 - see riskScoring.ts) over a from-scratch
    // likelihood x impact estimate. Nothing in this app ever populates a separate
    // residualLikelihood/residualImpact field, so the old fallback chain here always bottomed
    // out at plain likelihood x impact - silently equal to inherent, with every control's effect
    // (real or estimated) invisible to this stat regardless of what RiskDetail.tsx showed.
    const inherentLikelihood = mapLikelihoodToNumber(
      fields.inherentLikelihood || fields.inherent_likelihood || fields.likelihood || 1,
    );
    const inherentImpact = mapImpactToNumber(
      fields.inherentImpact || fields.inherent_impact || fields.impact || 1,
    );
    const inherentScore = parseRiskScore(fields.inherent_risk) ?? inherentLikelihood * inherentImpact;
    totalInherentRisk += inherentScore;
    if (inherentScore >= 15) criticalInherent += 1;

    const residualScore = parseRiskScore(fields.residual_risk) ?? inherentScore;
    totalResidualRisk += residualScore;
    if (residualScore >= 15) critical += 1;

    const response = String(
      fields.risk_response || fields.riskResponse || fields.response || fields.status || fields.risk_status || '',
    );
    if (response.toLowerCase().includes('mitigat')) mitigated += 1;
  });

  return {
    total,
    critical,
    criticalInherent,
    avgInherentRisk: total > 0 ? (totalInherentRisk / total).toFixed(1) : 0,
    avgResidualRisk: total > 0 ? (totalResidualRisk / total).toFixed(1) : 0,
    mitigated,
  };
};

const computeControlEffectiveness = (controls: Control[]) => {
  const counts: Record<string, number> = {
    'Highly Effective': 0,
    Effective: 0,
    'Partially Effective': 0,
    'Not Effective': 0,
  };

  if (!controls || controls.length === 0) {
    return { labels: Object.keys(counts), percentages: Object.values(counts) };
  }

  controls.forEach((control) => {
    let effectiveness: any =
      control.fields?.control_effectiveness ||
      control.fields?.effectiveness ||
      control.fields?.maturityLevel ||
      (control as any).effectiveness ||
      '';

    const score = parseEffectivenessScore(effectiveness);
    if (score !== null) {
      if (score >= 80) effectiveness = 'Highly Effective';
      else if (score >= 60) effectiveness = 'Effective';
      else if (score >= 40) effectiveness = 'Partially Effective';
      else effectiveness = 'Not Effective';
    }

    if (!effectiveness || !Object.prototype.hasOwnProperty.call(counts, effectiveness)) effectiveness = 'Not Effective';
    counts[String(effectiveness)] += 1;
  });

  const total = controls.length || 1;
  return {
    labels: Object.keys(counts),
    percentages: Object.values(counts).map((count) => (count / total) * 100),
  };
};

const computeControlsByType = (controls: Control[]) => {
  const typeCounts: Record<string, number> = {};
  controls.forEach((control) => {
    const type = String(control.controlType || control.type || 'Other');
    typeCounts[type] = (typeCounts[type] || 0) + 1;
  });
  return { labels: Object.keys(typeCounts), values: Object.values(typeCounts) };
};

const computeControlImplementation = (controls: Control[]) => {
  const statusCounts: Record<string, number> = {
    Planning: 0,
    'In-progress': 0,
    Completed: 0,
    'On-hold': 0,
  };

  controls.forEach((control) => {
    const raw = String(control.fields?.implementation_status || (control as any).implementationStatus || '');
    const statusLower = raw.toLowerCase().trim().replace(/_/g, '-').replace(/ /g, '-');

    if (statusLower === 'planning') statusCounts.Planning += 1;
    else if (statusLower === 'in-progress') statusCounts['In-progress'] += 1;
    else if (statusLower === 'completed' || statusLower === 'implemented') statusCounts.Completed += 1;
    else if (statusLower === 'on-hold') statusCounts['On-hold'] += 1;
  });

  return { labels: Object.keys(statusCounts), values: Object.values(statusCounts) };
};

const computeControlMaturity = (controls: Control[]) => {
  // Buckets match maturityToScore's own 0-5 scale (MATURITY_LABEL_SCORE in controlConformity.ts)
  // - 'Unassessed' IS score 0 on that scale, not a separate 7th bucket, so a control whose
  // current_maturity is blank/unrecognised/literally 'Unassessed' all land in the same place.
  const maturityCounts: Record<string, number> = {
    'Unassessed': 0,
    'Initial': 0,
    'Repeatable': 0,
    'Defined': 0,
    'Managed': 0,
    'Optimized': 0,
  };
  const labels = Object.keys(maturityCounts);

  controls.forEach((control) => {
    const raw = control.fields?.current_maturity || (control as any).currentMaturity || 'Unassessed';
    const score = maturityToScore(raw);
    const label = score !== null ? (labels[score] || 'Unassessed') : 'Unassessed';
    maturityCounts[label] += 1;
  });

  return { labels, values: Object.values(maturityCounts) };
};

const computeControlCategory = (controls: Control[]) => {
  const categoryCounts: Record<string, number> = {};
  controls.forEach((control) => {
    const category = String(
      control.fields?.control_category || control.fields?.control_type || (control as any).controlCategory || 'Other'
    );
    categoryCounts[category] = (categoryCounts[category] || 0) + 1;
  });

  const sorted = Object.entries(categoryCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 7);

  return {
    labels: sorted.map(([label]) => label),
    values: sorted.map(([, value]) => value),
  };
};

const computeControlTesting = (controls: Control[]) => {
  const testingCounts: Record<string, number> = {
    Tested: 0,
    'Due Soon': 0,
    Overdue: 0,
    'Not Tested': 0,
  };

  const now = new Date();

  controls.forEach((control) => {
    const lastTest = control.fields?.last_test_date || (control as any).lastTestDate;
    const nextTest = control.fields?.next_test_date || (control as any).nextTestDate;

    if (lastTest) {
      if (nextTest) {
        const nextDate = new Date(nextTest);
        const daysUntil = Math.ceil((nextDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        if (daysUntil < 0) testingCounts.Overdue += 1;
        else if (daysUntil <= 30) testingCounts['Due Soon'] += 1;
        else testingCounts.Tested += 1;
      } else {
        testingCounts.Tested += 1;
      }
    } else {
      testingCounts['Not Tested'] += 1;
    }
  });

  return { labels: Object.keys(testingCounts), values: Object.values(testingCounts) };
};

const computeFrameworkCoverage = (controls: Control[]) => {
  const frameworkData: Record<
    string,
    {
      name: string;
      total: number;
      planning: number;
      inProgress: number;
      completed: number;
      onHold: number;
      notStarted: number;
    }
  > = {};

  const frameworkNameToId: Record<string, string> = {};
  controls.forEach((control) => {
    const fwId = String(control.fields?.frameworkId || control.frameworkId || '');
    const fwName = String(control.fields?.framework || control.framework || '');
    if (fwId && fwName && !frameworkNameToId[fwName]) frameworkNameToId[fwName] = fwId;
  });

  controls.forEach((control) => {
    let fwId = String(control.fields?.frameworkId || control.frameworkId || '');
    const fwName = String(control.fields?.framework || control.framework || '');

    if (!fwId && fwName && frameworkNameToId[fwName]) fwId = frameworkNameToId[fwName];
    if (!fwId && fwName) fwId = fwName;
    if (!fwId) return;

    if (!frameworkData[fwId]) {
      frameworkData[fwId] = {
        name: fwName || fwId,
        total: 0,
        planning: 0,
        inProgress: 0,
        completed: 0,
        onHold: 0,
        notStarted: 0,
      };
    }

    frameworkData[fwId].total += 1;

    const raw = String(control.fields?.implementation_status || (control as any).implementationStatus || '');
    const statusLower = raw.toLowerCase().trim().replace(/_/g, '-').replace(/ /g, '-');

    if (statusLower === 'planning') frameworkData[fwId].planning += 1;
    else if (statusLower === 'in-progress') frameworkData[fwId].inProgress += 1;
    else if (statusLower === 'completed' || statusLower === 'implemented') frameworkData[fwId].completed += 1;
    else if (statusLower === 'on-hold') frameworkData[fwId].onHold += 1;
    else frameworkData[fwId].notStarted += 1;
  });

  const frameworks: string[] = [];
  const planning: number[] = [];
  const inProgress: number[] = [];
  const completed: number[] = [];
  const onHold: number[] = [];
  const notStarted: number[] = [];

  Object.values(frameworkData).forEach((fw) => {
    if (fw.total <= 0) return;
    frameworks.push(fw.name);
    planning.push((fw.planning / fw.total) * 100);
    inProgress.push((fw.inProgress / fw.total) * 100);
    completed.push((fw.completed / fw.total) * 100);
    onHold.push((fw.onHold / fw.total) * 100);
    notStarted.push((fw.notStarted / fw.total) * 100);
  });

  return { frameworks, planning, inProgress, completed, onHold, notStarted };
};

const computeAverageEffectiveness = (controls: Control[]) => {
  let totalScore = 0;
  let scoredControls = 0;
  const breakdown: Record<string, number> = {
    'Highly Effective (80-100%)': 0,
    'Effective (60-79%)': 0,
    'Partially Effective (40-59%)': 0,
    'Not Effective (0-39%)': 0,
    'Not Assessed': 0,
  };

  controls.forEach((control) => {
    const raw = control.fields?.control_effectiveness || control.fields?.effectiveness || '';
    const score = parseEffectivenessScore(raw);

    if (score !== null && !Number.isNaN(score)) {
      totalScore += score;
      scoredControls += 1;

      if (score >= 80) breakdown['Highly Effective (80-100%)'] += 1;
      else if (score >= 60) breakdown['Effective (60-79%)'] += 1;
      else if (score >= 40) breakdown['Partially Effective (40-59%)'] += 1;
      else breakdown['Not Effective (0-39%)'] += 1;
    } else {
      breakdown['Not Assessed'] += 1;
    }
  });

  const average = scoredControls > 0 ? (totalScore / scoredControls).toFixed(1) : 0;

  return {
    average,
    total: controls.length,
    scored: scoredControls,
    breakdown,
  };
};

const computeEffectivenessByDepartment = (
  controls: Control[],
  departments: Department[],
  raciAssignments: RaciAssignment[],
  people: Person[],
) => {
  const deptMap: Record<string, string> = {};
  departments.forEach((dept) => {
    const deptId = String(dept.departmentId || dept.id || '');
    if (!deptId) return;
    deptMap[deptId] = String(dept.department_name || dept.name || deptId);
  });

  const personDeptMap: Record<string, string> = {};
  people.forEach((person) => {
    const personId = String(person.personId || person.id || '');
    if (!personId) return;
    const deptId = String(person.departmentId || person.department || '');
    const deptName = deptMap[deptId] || String(person.departmentName || person.department || 'Unassigned');
    personDeptMap[personId] = deptName;
  });

  const deptScores: Record<string, number> = {};
  const deptCounts: Record<string, number> = {};

  controls.forEach((control) => {
    const controlId = String(control.controlId || control.id || '');

    const controlRaci = raciAssignments.filter((r) => (r.controlId === controlId || r.artefactId === controlId) && (r.role === 'A' || r.role === 'R'));

    let deptName = 'Unassigned';
    if (controlRaci.length > 0) {
      const accountable = controlRaci.find((r) => r.role === 'A');
      const responsible = controlRaci.find((r) => r.role === 'R');
      const assignment = accountable || responsible;
      if (assignment?.personId && personDeptMap[String(assignment.personId)]) {
        deptName = personDeptMap[String(assignment.personId)];
      }
    }

    const raw = control.fields?.control_effectiveness || control.fields?.effectiveness || '';
    const score = parseEffectivenessScore(raw);

    if (score !== null && !Number.isNaN(score)) {
      deptScores[deptName] = (deptScores[deptName] || 0) + score;
      deptCounts[deptName] = (deptCounts[deptName] || 0) + 1;
    }
  });

  const deptAverages = Object.keys(deptScores)
    .map((dept) => ({
      name: dept,
      average: Number.parseFloat((deptScores[dept] / deptCounts[dept]).toFixed(1)),
    }))
    .sort((a, b) => b.average - a.average)
    .slice(0, 10);

  return {
    labels: deptAverages.map((d) => d.name),
    values: deptAverages.map((d) => d.average),
  };
};

const computeIssueStatus = (issues: Issue[]) => {
  const statusCounts: Record<string, number> = {
    Open: 0,
    'In-progress': 0,
    Resolved: 0,
    Closed: 0,
  };

  issues.forEach((issue) => {
    const raw = String(
      issue.fields?.status ||
        (issue as any).status ||
        issue.fields?.issue_status ||
        (issue as any).issue_status ||
        'Open',
    );

    let normalized = raw;
    if (raw.toLowerCase() === 'in progress' || raw.toLowerCase() === 'in-progress') normalized = 'In-progress';
    else if (!Object.prototype.hasOwnProperty.call(statusCounts, raw)) {
      const matched = Object.keys(statusCounts).find((k) => k.toLowerCase() === raw.toLowerCase());
      normalized = matched || 'Open';
    }

    if (Object.prototype.hasOwnProperty.call(statusCounts, normalized)) statusCounts[normalized] += 1;
  });

  return { labels: Object.keys(statusCounts), values: Object.values(statusCounts) };
};

const computeIncidentSeverity = (incidents: Incident[]) => {
  const severityCounts: Record<string, number> = {
    Low: 0,
    Medium: 0,
    High: 0,
    Critical: 0,
  };

  incidents.forEach((incident) => {
    const sev = String(
      incident.fields?.severity ||
        (incident as any).severity ||
        incident.fields?.incident_severity ||
        (incident as any).incident_severity ||
        'Low',
    );
    if (Object.prototype.hasOwnProperty.call(severityCounts, sev)) severityCounts[sev] += 1;
  });

  return { labels: Object.keys(severityCounts), values: Object.values(severityCounts) };
};

const computeIssuePriority = (issues: Issue[]) => {
  const priorityCounts: Record<string, number> = {
    Critical: 0,
    High: 0,
    Medium: 0,
    Low: 0,
  };

  issues.forEach((issue) => {
    const raw = String(
      issue.fields?.priority ||
        (issue as any).priority ||
        issue.fields?.issue_priority ||
        (issue as any).issue_priority ||
        'Medium',
    );

    if (Object.prototype.hasOwnProperty.call(priorityCounts, raw)) priorityCounts[raw] += 1;
    else {
      const normalized = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
      if (Object.prototype.hasOwnProperty.call(priorityCounts, normalized)) priorityCounts[normalized] += 1;
      else priorityCounts.Medium += 1;
    }
  });

  return { labels: Object.keys(priorityCounts), values: Object.values(priorityCounts) };
};

const computeIssueDepartment = (issues: Issue[], departments: Department[]) => {
  const deptMap: Record<string, string> = {};
  departments.forEach((dept) => {
    const deptId = String(dept.departmentId || dept.id || '');
    if (!deptId) return;
    deptMap[deptId] = String(dept.department_name || dept.name || deptId);
  });

  const departmentCounts: Record<string, number> = {};

  issues.forEach((issue) => {
    const rawDeptId = String(
      issue.fields?.department ||
        (issue as any).department ||
        issue.fields?.affected_department ||
        (issue as any).affected_department ||
        issue.fields?.business_unit ||
        (issue as any).business_unit ||
        issue.fields?.business_function ||
        (issue as any).business_function ||
        (issue as any).issue_businessfunction ||
        'Unassigned',
    );

    const deptName = deptMap[rawDeptId] || rawDeptId;
    departmentCounts[deptName] = (departmentCounts[deptName] || 0) + 1;
  });

  const sorted = Object.entries(departmentCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  return { labels: sorted.map(([dept]) => dept), values: sorted.map(([, count]) => count) };
};

const Card: React.FC<{ title: React.ReactNode; children: React.ReactNode }> = ({ title, children }) => {
  return (
    <div className="np-card mb-4">
      <div className="np-card-head">
        <h2>{title}</h2>
      </div>
      <div className="np-card-body">{children}</div>
    </div>
  );
};

const ChartOrEmpty: React.FC<{
  isLoading: boolean;
  isError: boolean;
  empty: boolean;
  emptyText: string;
  children: React.ReactNode;
}> = ({ isLoading, isError, empty, emptyText, children }) => {
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (isError) {
    return <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Failed to load report data.</div>;
  }

  if (empty) {
    return (
      <div className="text-center np-sub p-4">
        <i className="ri-bar-chart-2-line ri-24px d-block mb-2" style={{ color: 'var(--bs-secondary-color)' }}></i>
        {emptyText}
      </div>
    );
  }

  return <>{children}</>;
};

// ─── Gap Analysis ────────────────────────────────────────────────────────────

// Real 0-5 scale (maturityToScore/MATURITY_LABEL_SCORE in controlConformity.ts) - 'Unassessed'
// IS score 0, not a separate 7th bucket. currentScore/targetScore/gap below use maturityToScore
// directly rather than a local reimplementation - see controlConformity.ts's own header for why
// a digit-prefixed scale ('0 - Non-Existent' etc, what this file used to assume) never matches
// any real stored current_maturity/target_maturity value.
const MATURITY_SHORT_LABELS = ['Unassessed', 'Initial', 'Repeatable', 'Defined', 'Managed', 'Optimized'];

interface GapRow {
  controlId: string;
  matrixId: string;
  userControlId: string;
  name: string;
  department: string;
  responsible: string;
  hasRaciOwnership: boolean;
  currentLabel: string;
  currentScore: number | null;
  targetLabel: string;
  targetScore: number | null;
  gap: number | null;
  implementationStatus: string;
  belowRepeatable: boolean;
  needsRemediation: boolean;
  evidenceCount: number;
  hasEvidence: boolean;
  isIsoScoped: boolean;
  isInScope: boolean;
  framework: string;
  raciDepts: string[];
  conformityRisk: boolean;
  conformityReasons: string[];
  conformityChecked: Array<{ label: string; passed: boolean }>;
  riskAccepted: boolean;
  riskAcceptanceReason: string;
  riskAcceptanceOwner: string;
  riskAcceptanceUntil: string;
  riskAcceptanceReviewPeriod: string;
  riskAcceptanceDate: string;
  acceptanceExpired: boolean;
}

type GapSortCol = 'name' | 'department' | 'responsible' | 'currentScore' | 'gap' | 'implementationStatus' | 'evidenceCount' | 'conformityRisk';

interface GapAnalysisResult {
  rows: GapRow[];
  summary: {
    total: number;
    belowRepeatable: number;
    pctBelowRepeatable: number;
    avgCurrentMaturity: string;
    avgGap: string;
    needsRemediation: number;
    pctNeedsRemediation: number;
    conformityRisk: number;
    withEvidence: number;
    pctWithEvidence: number;
    withRaci: number;
    pctWithRaci: number;
  };
  maturityDistrib: { labels: string[]; values: number[] };
  deptBreakdown: Array<{ name: string; count: number; belowCount: number; conformityRiskCount: number }>;
}

const computeGapAnalysis = (
  controls: Control[],
  raciAssignments: RaciAssignment[],
  people: Person[],
  departments: Department[],
  documents: any[],
  evidenceItems: any[],
): GapAnalysisResult => {
  // Build department name lookup
  const deptMap: Record<string, string> = {};
  departments.forEach((d) => {
    const id = String(d.departmentId || d.id || '');
    if (id) deptMap[id] = String(d.department_name || d.name || id);
  });

  // Build person name lookup
  const personNameMap: Record<string, string> = {};
  const personDeptMap: Record<string, string> = {};
  people.forEach((p) => {
    const id = String((p as any).personId || (p as any).id || '');
    if (id) personNameMap[id] = String((p as any).personName || [((p as any).firstName || ''), ((p as any).lastName || '')].filter(Boolean).join(' ').trim() || id);
    const deptId = String((p as any).departmentId || '');
    if (id && deptId && deptMap[deptId]) personDeptMap[id] = deptMap[deptId];
  });

  // Build evidence count per control
  const controlDocCount: Record<string, number> = {};
  documents.forEach((doc) => {
    if (doc.entityType === 'control' && doc.entityId) {
      controlDocCount[String(doc.entityId)] = (controlDocCount[String(doc.entityId)] || 0) + 1;
    }
  });

  // Build evidence lookup by evidenceId
  const evidenceById: Record<string, any> = {};
  evidenceItems.forEach((e) => {
    const eid = String(e.evidenceId || e.id || '');
    if (eid) evidenceById[eid] = e;
  });

  // Today's date (midnight) for date comparisons
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const raciByControl: Record<string, RaciAssignment[]> = {};
  raciAssignments.forEach((r) => {
    const cid = String(r.controlId || r.artefactId || '');
    if (!cid) return; // skip non-control RACI records (riskId/issueId etc.)
    if (!raciByControl[cid]) raciByControl[cid] = [];
    raciByControl[cid].push(r);
  });

  const rows: GapRow[] = controls.map((control) => {
    const controlId = String((control as any).controlId || control.id || '');
    const matrixId = String((control as any).matrixId || control.matrixId || '');
    const userControlId = String(control.fields?.control_id || '');
    const name = String(
      control.fields?.control_name ||
      control.fields?.name ||
      control.fields?.title ||
      control.fields?.control_title ||
      controlId ||
      'Unnamed Control',
    );

    // Try known field names first, then fall back to scanning all field values
    // for a match against the deptMap (handles custom matrix field IDs)
    const knownDeptId = String(
      control.fields?.department_id ||
      control.fields?.departmentId ||
      control.fields?.department ||
      (control as any).departmentId ||
      '',
    );
    const rawDeptId = (knownDeptId && deptMap[knownDeptId])
      ? knownDeptId
      : Object.values(control.fields || {}).find((v) => typeof v === 'string' && deptMap[v]) as string || knownDeptId;
    const department = (rawDeptId && deptMap[rawDeptId]) ? deptMap[rawDeptId] : (rawDeptId || '');

    const controlRaci = raciByControl[controlId] || [];
    const resp = controlRaci.find((r) => r.role === 'R') || controlRaci.find((r) => r.role === 'A');
    const responsible = resp
      ? (personNameMap[String(resp.personId || '')] || 'Unassigned')
      : 'Unassigned';

    const currentLabel = String(control.fields?.current_maturity || '');
    const targetLabel  = String(control.fields?.target_maturity  || '');
    const currentScore = maturityToScore(currentLabel);
    const targetScore  = maturityToScore(targetLabel);
    const gap = currentScore !== null && targetScore !== null ? targetScore - currentScore : null;
    const needsRemediation = gap !== null && gap > 0;
    // File uploads attached directly to this control via document manager
    const docUploadCount = controlDocCount[controlId] || 0;

    const framework = String(control.fields?.framework || '');
    const assignedScope = String(control.fields?.assigned_scope || '');
    const isIsoScoped = /iso/i.test(framework) && assignedScope !== 'Out of Scope' && assignedScope !== 'Not Applicable';
    const isInScope = assignedScope !== 'Out of Scope' && assignedScope !== 'Not Applicable';
    const hasRaciOwnership = controlRaci.some((r) => r.role === 'R' || r.role === 'A');
    const implStatusLower = String(control.fields?.implementation_status || '').toLowerCase();
    // Departments derived from R/A RACI persons
    const raciDepts = Array.from(new Set(
      controlRaci
        .filter((r) => r.role === 'R' || r.role === 'A')
        .map((r) => personDeptMap[String(r.personId || '')])
        .filter(Boolean) as string[]
    ));

    // Resolve linked evidence artefacts — try multiple field name conventions
    const linkedEvidenceObjs: any[] = (() => {
      const raw = (control as any).linked_artefacts;
      if (!Array.isArray(raw) || raw.length === 0) return [];
      return raw
        .filter((la: any) => {
          const t = String(la.artefact_type || la.type || '').toLowerCase();
          return !t || t === 'evidence';
        })
        .map((la: any) => {
          const id = String(la.artefact_id || la.targetId || la.sourceId || la.artefactId || la.evidenceId || la.id || '');
          return id ? evidenceById[id] : undefined;
        })
        .filter(Boolean);
    })();

    // Total evidence = uploaded files + resolved evidence register items only (not arbitrary linked artefacts)
    const evidenceCount = docUploadCount + linkedEvidenceObjs.length;
    const hasEvidence = evidenceCount > 0;

    const conformityReasons: string[] = [];
    const conformityChecked: Array<{ label: string; passed: boolean }> = [];
    if (isInScope) {
      // 1. Implementation status - real stored values are 'Implemented'/'not_implemented'/
      //    'Not Applicable' (confirmed live), never 'Completed' or any of the other keywords
      //    this used to guess at - see controlConformity.ts.
      const isImplemented = isImplementationComplete(implStatusLower);
      if (!isImplemented) conformityReasons.push('Not fully implemented');
      conformityChecked.push({ label: 'Fully implemented', passed: isImplemented });

      // 2. Below minimum maturity — blank (never visited) and a literal 'Unassessed' label
      //    (visited, deliberately left unassessed) both read as "not yet assessed" rather than
      //    "assessed and failing", matching StatementOfApplicabilityPage.tsx's own convention -
      //    maturityToScore itself can't tell these apart since 'unassessed' scores 0, same as a
      //    genuinely low real rating would if one existed.
      const maturityBlank = currentLabel === '';
      const maturityUnassessed = currentLabel.trim().toLowerCase() === 'unassessed';
      const aboveMin = maturityBlank || (!maturityUnassessed && currentScore !== null && currentScore >= 2);
      if (!aboveMin) {
        conformityReasons.push(
          (maturityBlank || maturityUnassessed)
            ? 'Maturity not yet assessed'
            : 'Below minimum maturity (< Repeatable)'
        );
      }
      conformityChecked.push({ label: 'Maturity assessed & ≥ Repeatable', passed: aboveMin });

      // 3. No evidence linked at all
      if (!hasEvidence) conformityReasons.push('No evidence linked');
      conformityChecked.push({ label: 'Evidence linked', passed: hasEvidence });

      // 4. No RACI ownership
      if (!hasRaciOwnership) conformityReasons.push('No RACI ownership (R or A)');
      conformityChecked.push({ label: 'RACI ownership assigned', passed: hasRaciOwnership });

      // 5. Overdue next test / review date on the control
      const nextTestStr = String(
        control.fields?.next_test_date ||
        control.fields?.control_next_review ||
        control.fields?.next_review_date || ''
      );
      if (nextTestStr) {
        const nextTest = new Date(nextTestStr);
        const testOverdue = !isNaN(nextTest.getTime()) && nextTest < today;
        if (testOverdue) conformityReasons.push('Test / review overdue');
        conformityChecked.push({ label: 'Test / review date current', passed: !testOverdue });
      }

      // 6 & 7. Linked evidence checks
      if (linkedEvidenceObjs.length > 0) {
        // Only evaluate review currency on items that actually have a next review date scheduled.
        // Items with no scheduled date are NOT counted as "current" — omit rather than show a false green tick.
        // next_due_date is what every real write path actually populates (CollectionScheduleCard.tsx,
        // lambda/prod-nimbus-evidence's review_submission()) - evidence_next_review/next_review_date
        // are never written anywhere, which made this check structurally unable to ever fire.
        const itemsWithSchedule = linkedEvidenceObjs.filter((ev: any) => !!ev.fields?.next_due_date);
        const overdueItems = linkedEvidenceObjs.filter((ev: any) => {
          const reviewStr = String(ev.fields?.next_due_date || '');
          if (reviewStr) {
            const reviewDate = new Date(reviewStr);
            if (!isNaN(reviewDate.getTime()) && reviewDate < today) return true;
          }
          const st = String(ev.status || ev.fields?.status || ev.fields?.evidence_status || '').toLowerCase();
          return st === 'expired' || st === 'overdue' || st === 'rejected';
        });
        // Only add the check if there's at least one scheduled item — never show as a false green tick
        if (itemsWithSchedule.length > 0 || overdueItems.length > 0) {
          if (overdueItems.length > 0) conformityReasons.push('Evidence overdue for review');
          conformityChecked.push({ label: 'Evidence review current', passed: overdueItems.length === 0 });
        }

        const hasUnreviewedEvidence = linkedEvidenceObjs.some((ev: any) => {
          const lastReviewed = String(
            ev.fields?.evidence_last_reviewed ||
            ev.fields?.last_reviewed ||
            ev.fields?.last_review_date || ''
          );
          return !lastReviewed;
        });
        if (hasUnreviewedEvidence) conformityReasons.push('Evidence not yet reviewed');
        conformityChecked.push({ label: 'All evidence reviewed at least once', passed: !hasUnreviewedEvidence });
      }
    }
    const conformityRisk = conformityReasons.length > 0;

    const riskAcceptanceReason = String(control.fields?.risk_acceptance_reason || '').trim();
    const riskAcceptanceOwner  = String(control.fields?.risk_acceptance_owner  || '').trim();
    const riskAcceptanceUntil  = String(control.fields?.risk_acceptance_until  || '').trim();
    const riskAcceptanceReviewPeriod = String(control.fields?.risk_acceptance_review_period || '').trim();
    const riskAcceptanceDate = String(control.fields?.risk_acceptance_date || '').trim();
    const acceptanceUntilDate  = riskAcceptanceUntil ? new Date(riskAcceptanceUntil) : null;
    const acceptanceExpired = acceptanceUntilDate
      ? (!isNaN(acceptanceUntilDate.getTime()) && acceptanceUntilDate < today)
      : false;
    const riskAccepted = !!(riskAcceptanceReason && riskAcceptanceUntil && !acceptanceExpired);

    return {
      controlId,
      matrixId,
      userControlId,
      name,
      department,
      responsible,
      hasRaciOwnership,
      currentLabel,
      currentScore,
      targetLabel,
      targetScore,
      gap,
      implementationStatus: String(control.fields?.implementation_status || ''),
      belowRepeatable: currentScore !== null && currentScore < 2,
      needsRemediation,
      evidenceCount,
      hasEvidence,
      isIsoScoped,
      isInScope,
      framework,
      raciDepts,
      conformityRisk,
      conformityReasons,
      conformityChecked,
      riskAccepted,
      riskAcceptanceReason,
      riskAcceptanceOwner,
      riskAcceptanceUntil,
      riskAcceptanceReviewPeriod,
      riskAcceptanceDate,
      acceptanceExpired,
    };
  });

  const withCurrent = rows.filter((r) => r.currentScore !== null);
  const avgCurrent = withCurrent.length
    ? (withCurrent.reduce((s, r) => s + r.currentScore!, 0) / withCurrent.length).toFixed(1)
    : '—';
  const withGap = rows.filter((r) => r.gap !== null);
  const avgGap = withGap.length
    ? (withGap.reduce((s, r) => s + r.gap!, 0) / withGap.length).toFixed(1)
    : '—';
  const belowRepeatableCount = rows.filter((r) => r.belowRepeatable).length;
  const pctBelow = rows.length ? Math.round((belowRepeatableCount / rows.length) * 100) : 0;
  const needsRemediationCount = rows.filter((r) => r.needsRemediation).length;
  const pctNeedsRemediation = rows.length ? Math.round((needsRemediationCount / rows.length) * 100) : 0;
  const withEvidenceCount = rows.filter((r) => r.hasEvidence).length;
  const pctWithEvidence = rows.length ? Math.round((withEvidenceCount / rows.length) * 100) : 0;

  // Count controls with at least an R or A in RACI
  const raciByControlForSummary: Record<string, boolean> = {};
  raciAssignments.forEach((r) => {
    const cid = String(r.controlId || r.artefactId || '');
    if ((r.role === 'R' || r.role === 'A') && cid) raciByControlForSummary[cid] = true;
  });
  const withRaciCount = rows.filter((r) => raciByControlForSummary[r.controlId]).length;
  const pctWithRaci = rows.length ? Math.round((withRaciCount / rows.length) * 100) : 0;

  // 6 buckets matching MATURITY_SHORT_LABELS - index 0 (Unassessed) covers both a real score of
  // 0 and a currentScore that couldn't be parsed at all (blank/unrecognised), same convention
  // computeControlMaturity uses.
  const distribCounts = [0, 0, 0, 0, 0, 0];
  rows.forEach((r) => { distribCounts[r.currentScore ?? 0] += 1; });

  // Department breakdown — use RACI R/A person departments, fall back to control's own department
  const deptCountMap: Record<string, { count: number; belowCount: number; conformityRiskCount: number }> = {};
  rows.forEach((r) => {
    const depts = r.raciDepts.length > 0 ? r.raciDepts : [r.department || 'Unassigned'];
    depts.forEach((dept) => {
      if (!deptCountMap[dept]) deptCountMap[dept] = { count: 0, belowCount: 0, conformityRiskCount: 0 };
      deptCountMap[dept].count += 1;
      if (r.belowRepeatable) deptCountMap[dept].belowCount += 1;
      if (r.conformityRisk) deptCountMap[dept].conformityRiskCount += 1;
    });
  });
  const deptBreakdown = Object.entries(deptCountMap)
    .map(([name, { count, belowCount, conformityRiskCount }]) => ({ name, count, belowCount, conformityRiskCount }))
    .sort((a, b) => b.conformityRiskCount - a.conformityRiskCount)
    .slice(0, 8);

  return {
    rows,
    summary: {
      total: rows.length,
      belowRepeatable: belowRepeatableCount,
      pctBelowRepeatable: pctBelow,
      avgCurrentMaturity: avgCurrent,
      avgGap,
      needsRemediation: needsRemediationCount,
      pctNeedsRemediation,
      conformityRisk: rows.filter((r) => r.conformityRisk).length,
      withEvidence: withEvidenceCount,
      pctWithEvidence,
      withRaci: withRaciCount,
      pctWithRaci,
    },
    maturityDistrib: { labels: MATURITY_SHORT_LABELS, values: distribCounts },
    deptBreakdown,
  };
};

async function fetchIsmsConfig(): Promise<{ exists: boolean; columns?: FlowColumn[] } | undefined> {
  try {
    const res = await fetch(`${API_BASE_URL}/config/isms-journey`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    if (!res.ok) return undefined;
    return res.json();
  } catch {
    return undefined;
  }
}

export const ReportsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialTab = (['risk','control','compliance','issues','gap'].includes(searchParams.get('tab') ?? '') ? searchParams.get('tab') : 'risk') as 'risk' | 'control' | 'compliance' | 'issues' | 'gap';
  const [activeTab, setActiveTab] = useState<'risk' | 'control' | 'compliance' | 'issues' | 'gap'>(initialTab);
  const [gapShowBelowOnly, setGapShowBelowOnly] = useState(false);
  const [gapShowConformityOnly, setGapShowConformityOnly] = useState(false);
  const [gapSearch, setGapSearch] = useState('');
  const [gapSortCol, setGapSortCol] = useState<GapSortCol>('currentScore');
  const [gapSortDir, setGapSortDir] = useState<'asc' | 'desc'>('asc');
  const [gapFrameworkFilter, setGapFrameworkFilter] = useState<string>('all');
  const [gapDeptFilter, setGapDeptFilter] = useState<string>('all');
  const [gapMaturityFilter, setGapMaturityFilter] = useState<string>('all');
  const [gapStatusFilter, setGapStatusFilter] = useState<string>('all');
  const [acceptSearch, setAcceptSearch] = useState('');
  const [acceptStatusFilter, setAcceptStatusFilter] = useState<string>('all');
  const [acceptFrameworkFilter, setAcceptFrameworkFilter] = useState<string>('all');
  const [acceptSortCol, setAcceptSortCol] = useState<string>('name');
  const [acceptSortDir, setAcceptSortDir] = useState<'asc' | 'desc'>('asc');

  // ── ISMS Journey progress ─────────────────────────────────────────────────
  const [journeyChecks] = useState<Set<string>>(() => loadFlowChecks());
  const { data: journeyConfig } = useQuery({
    queryKey: ['ismsFlowConfig'],
    queryFn:  fetchIsmsConfig,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Merge live config with bundled defaults (same logic as IsmsJourneyPage)
  const journeyColumns: FlowColumn[] = (() => {
    if (journeyConfig?.exists && journeyConfig.columns?.length) {
      const savedIds = new Set(journeyConfig.columns.map((c: FlowColumn) => c.id));
      return [...journeyConfig.columns, ...FLOW_COLUMNS.filter(c => !savedIds.has(c.id))];
    }
    return FLOW_COLUMNS;
  })();

  // Split non-milestone steps at the docs-milestone-stage1 boundary
  const { stage1Steps, stage2Steps } = (() => {
    const s1 = [] as typeof journeyColumns[0]['steps'];
    const s2 = [] as typeof journeyColumns[0]['steps'];
    let pastMilestone = false;
    for (const col of journeyColumns) {
      for (const step of col.steps) {
        if (step.id === 'docs-milestone-stage1') { pastMilestone = true; continue; }
        if (step.isMilestone) continue;
        if (pastMilestone) s2.push(step); else s1.push(step);
      }
    }
    return { stage1Steps: s1, stage2Steps: s2 };
  })();

  const stage1Done = stage1Steps.filter(s => journeyChecks.has(s.id)).length;
  const stage2Done = stage2Steps.filter(s => journeyChecks.has(s.id)).length;
  const stage1Pct  = stage1Steps.length > 0 ? Math.round((stage1Done / stage1Steps.length) * 100) : 0;
  const stage2Pct  = stage2Steps.length > 0 ? Math.round((stage2Done / stage2Steps.length) * 100) : 0;

  const query = useQuery({
    queryKey: ['reports'],
    queryFn: async (): Promise<ReportsData> => {
      const [risks, controls, departments, issues, incidents, raciAssignments, people, documents, evidenceItems] = await Promise.all([
        riskApi.listAll(),
        listApi.controls(),
        departmentsApi.getAll(),
        listApi.issues(),
        listApi.incidents(),
        raciApi.getAll(),
        peopleApi.getAll(),
        documentsApi.listAll().catch(() => []),
        listApi.evidence().catch(() => []),
      ]);

      return {
        risks: (risks || []) as Risk[],
        controls: (controls || []) as Control[],
        departments: (departments || []) as Department[],
        issues: (issues || []) as Issue[],
        incidents: (incidents || []) as Incident[],
        raciAssignments: (raciAssignments || []) as RaciAssignment[],
        people: (people || []) as Person[],
        documents: (documents || []) as any[],
        evidenceItems: (evidenceItems || []) as any[],
      };
    },
  });

  const computed = useMemo(() => {
    const risks = query.data?.risks || [];
    const controls = query.data?.controls || [];
    const departments = query.data?.departments || [];
    const issues = query.data?.issues || [];
    const incidents = query.data?.incidents || [];
    const raciAssignments = query.data?.raciAssignments || [];
    const people = query.data?.people || [];
    const documents = query.data?.documents || [];
    const evidenceItems = query.data?.evidenceItems || [];

    const riskStats = computeRiskStats(risks);

    return {
      riskStats,
      riskHeatmapSeries: computeRiskHeatmapSeries(risks),
      residualBandDistribution: computeResidualBandDistribution(risks),
      topRisks: computeTopRisks(risks),
      treatmentPipeline: computeTreatmentPipeline(risks),
      riskByCategory: computeRiskByCategory(risks),
      riskScoreByCategory: computeRiskScoreByCategory(risks),

      controlEffectiveness: computeControlEffectiveness(controls),
      controlsByType: computeControlsByType(controls),
      controlImplementation: computeControlImplementation(controls),
      controlMaturity: computeControlMaturity(controls),
      controlCategory: computeControlCategory(controls),
      controlTesting: computeControlTesting(controls),

      frameworkCoverage: computeFrameworkCoverage(controls),
      implementationAlignment: computeControlImplementation(controls),
      avgEffectiveness: computeAverageEffectiveness(controls),
      effectivenessByDept: computeEffectivenessByDepartment(controls, departments, raciAssignments, people),

      issueStatus: computeIssueStatus(issues),
      incidentSeverity: computeIncidentSeverity(incidents),
      issuePriority: computeIssuePriority(issues),
      issueDepartment: computeIssueDepartment(issues, departments),

      gapAnalysis: computeGapAnalysis(controls, raciAssignments, people, departments, documents, evidenceItems),
    };
  }, [query.data]);

  // Re-initialise tooltips when data loads (query is now in scope here)
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const bsTooltip = (window as any).bootstrap?.Tooltip;
      if (bsTooltip) {
        document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
          // Dispose any existing instance so HTML option is always re-read from the element
          bsTooltip.getInstance(el)?.dispose();
          new bsTooltip(el);
        });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [query.dataUpdatedAt, activeTab]);

  const exportToPDF = () => {
    exportReportsPdf({
      counts: { risks: query.data?.risks?.length || 0 },
      inherent: {
        total: computed.riskStats.total,
        critical: computed.riskStats.criticalInherent,
        avgScore: computed.riskStats.avgInherentRisk,
        heatmap: computed.riskHeatmapSeries,
        treatmentPipeline: computed.treatmentPipeline,
        byCategory: computed.riskByCategory,
      },
      residual: {
        total: computed.riskStats.total,
        critical: computed.riskStats.critical,
        avgScore: computed.riskStats.avgResidualRisk,
        bandDistribution: computed.residualBandDistribution,
        treatmentPipeline: computed.treatmentPipeline,
        byCategory: computed.riskByCategory,
      },
    });
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    const riskData = [
      ['Metric', 'Value'],
      ['Total Risks', computed.riskStats.total || 0],
      ['Critical Risks', computed.riskStats.critical || 0],
      ['Avg Inherent Risk', computed.riskStats.avgInherentRisk || 0],
      ['Avg Residual Risk', computed.riskStats.avgResidualRisk || 0],
      ['Mitigated Risks', computed.riskStats.mitigated || 0],
    ];

    const ws = XLSX.utils.aoa_to_sheet(riskData);
    XLSX.utils.book_append_sheet(wb, ws, 'Risk Summary');

    XLSX.writeFile(wb, 'mercell-nimbus-reports.xlsx');
  };

  const riskHeatmapOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'heatmap', height: 350, toolbar: { show: true } },
      dataLabels: { enabled: true, style: { colors: ['#fff'] } },
      colors: ['#56ca00'],
      plotOptions: {
        heatmap: {
          radius: 2,
          enableShades: true,
          shadeIntensity: 0.5,
          colorScale: {
            ranges: [
              { from: 0, to: 0, color: '#e4e2e8', name: 'None' },
              { from: 1, to: 2, color: '#56ca00', name: 'Low' },
              { from: 3, to: 5, color: '#ffb400', name: 'Medium' },
              { from: 6, to: 10, color: '#ff8a3d', name: 'High' },
              { from: 11, to: 999, color: '#ff4c51', name: 'Critical' },
            ],
          },
        },
      },
      xaxis: { categories: ['Impact 1', 'Impact 2', 'Impact 3', 'Impact 4', 'Impact 5'] },
      yaxis: { labels: { style: { fontSize: '12px' } } },
      legend: { show: true, position: 'bottom' },
    }),
    [],
  );

  const topRisksOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'bar', height: 350, toolbar: { show: true } },
      plotOptions: { bar: { horizontal: true, distributed: true, borderRadius: 8 } },
      colors: ['#ff4c51', '#ff4c51', '#ff8a3d', '#ff8a3d', '#ffb400', '#ffb400', '#56ca00', '#56ca00', '#56ca00', '#56ca00'],
      dataLabels: { enabled: true, style: { colors: ['#fff'] } },
      xaxis: { categories: computed.topRisks.labels },
      legend: { show: false },
    }),
    [computed.topRisks.labels],
  );

  const pipelineOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'bar', height: 280, toolbar: { show: true } },
      plotOptions: { bar: { horizontal: true, distributed: true, borderRadius: 8, barHeight: '70%' } },
      colors: ['#8c57ff', '#a67aff', '#c3a1ff', '#e0d0ff'],
      dataLabels: { enabled: true, style: { colors: ['#fff'] } },
      xaxis: { categories: computed.treatmentPipeline.labels },
      legend: { show: false },
    }),
    [computed.treatmentPipeline.labels],
  );

  const donutBase: ApexOptions = useMemo(
    () => ({
      chart: { type: 'donut', height: 280 },
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                fontSize: '14px',
                fontWeight: 600,
                formatter: (w: any) => (w?.globals?.seriesTotals || []).reduce((a: number, b: number) => a + b, 0),
              },
            },
          },
        },
      },
      legend: { show: true, position: 'bottom', fontSize: '13px' },
      dataLabels: { enabled: true },
    }),
    [],
  );

  const riskByCategoryOptions: ApexOptions = useMemo(
    () => ({
      ...donutBase,
      labels: computed.riskByCategory.labels,
      colors: ['#696cff', '#8e92ff', '#ff6384', '#36a2eb', '#ffce56', '#4bc0c0', '#9966ff'],
      tooltip: {
        y: {
          formatter: (val: number, ctx: any) => {
            const series = ctx?.w?.globals?.series || [];
            const total = series.reduce((a: number, b: number) => a + b, 0) || 1;
            const pct = ((val / total) * 100).toFixed(1);
            return `${val} (${pct}%)`;
          },
        },
      },
    }),
    [computed.riskByCategory.labels, donutBase],
  );

  const riskScoreByCategoryOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'bar', height: 350 },
      plotOptions: { bar: { horizontal: true, borderRadius: 5, borderRadiusApplication: 'end' } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: computed.riskScoreByCategory.labels,
        title: { text: 'Total Risk Score (sum of L×I)' },
      },
      yaxis: { title: { text: 'Risk Category' } },
      tooltip: {
        y: {
          formatter: (_: number, ctx: any) => {
            const index = ctx?.dataPointIndex ?? 0;
            const totalScore = computed.riskScoreByCategory.values[index] ?? 0;
            const count = computed.riskScoreByCategory.counts[index] ?? 0;
            const avg = Number(computed.riskScoreByCategory.avgScores[index] ?? 0).toFixed(1);
            return `Total: ${totalScore} | Count: ${count} | Avg: ${avg}`;
          },
        },
      },
      colors: ['#dc3545'],
    }),
    [computed.riskScoreByCategory],
  );

  const controlEffectivenessOptions: ApexOptions = useMemo(
    () => ({
      ...donutBase,
      labels: computed.controlEffectiveness.labels,
      colors: ['#28a745', '#ffc107', '#fd7e14', '#dc3545'],
      tooltip: { y: { formatter: (val: number) => `${val.toFixed(1)}%` } },
    }),
    [computed.controlEffectiveness.labels, donutBase],
  );

  const simpleBarOptions = (categories: string[], horizontal = false): ApexOptions => ({
    chart: { type: 'bar', height: 280, toolbar: { show: true } },
    plotOptions: { bar: { horizontal, borderRadius: 8 } },
    xaxis: horizontal ? undefined : { categories },
    yaxis: horizontal ? undefined : undefined,
    ...(horizontal ? { xaxis: { categories } } : {}),
    legend: { show: false },
    dataLabels: { enabled: true },
  });

  const frameworkCoverageOptions: ApexOptions = useMemo(
    () => ({
      series: [
        { name: 'Completed', data: computed.frameworkCoverage.completed },
        { name: 'In-progress', data: computed.frameworkCoverage.inProgress },
        { name: 'Planning', data: computed.frameworkCoverage.planning },
        { name: 'On-hold', data: computed.frameworkCoverage.onHold },
        { name: 'Not Started', data: computed.frameworkCoverage.notStarted },
      ],
      chart: { type: 'bar', height: 350, stacked: true, stackType: '100%', toolbar: { show: true } },
      plotOptions: { bar: { horizontal: true, borderRadius: 8 } },
      colors: ['#28a745', '#17a2b8', '#ffc107', '#fd7e14', '#6c757d'],
      stroke: { width: 1, colors: ['#fff'] },
      xaxis: {
        categories: computed.frameworkCoverage.frameworks,
        labels: { formatter: (val: any) => (typeof val === 'number' ? `${val.toFixed(0)}%` : val) },
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number) => (val > 0 ? `${val.toFixed(0)}%` : ''),
        style: { colors: ['#fff'] },
      },
      legend: { position: 'top', horizontalAlign: 'left', fontSize: '13px' },
      tooltip: { y: { formatter: (val: number) => `${val.toFixed(1)}%` } },
    }),
    [computed.frameworkCoverage],
  );

  const implementationAlignmentOptions: ApexOptions = useMemo(
    () => ({
      ...donutBase,
      labels: computed.implementationAlignment.labels,
      colors: ['#ffc107', '#17a2b8', '#28a745', '#6c757d'],
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total Controls',
                fontSize: '14px',
                fontWeight: 600,
                formatter: (w: any) => (w?.globals?.seriesTotals || []).reduce((a: number, b: number) => a + b, 0),
              },
            },
          },
        },
      },
    }),
    [computed.implementationAlignment.labels, donutBase],
  );

  const avgEffectivenessOptions: ApexOptions = useMemo(
    () => ({
      ...donutBase,
      labels: Object.keys(computed.avgEffectiveness.breakdown),
      colors: ['#28a745', '#17a2b8', '#ffc107', '#dc3545', '#6c757d'],
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total Controls',
                fontSize: '14px',
                fontWeight: 600,
                formatter: (w: any) => (w?.globals?.seriesTotals || []).reduce((a: number, b: number) => a + b, 0),
              },
            },
          },
        },
      },
    }),
    [computed.avgEffectiveness.breakdown, donutBase],
  );

  const effectivenessByDeptOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'bar', height: 400, toolbar: { show: true } },
      plotOptions: { bar: { horizontal: true, borderRadius: 8 } },
      colors: ['#696cff'],
      xaxis: {
        categories: computed.effectivenessByDept.labels,
        title: { text: 'Average Effectiveness (%)' },
      },
      dataLabels: { enabled: true, formatter: (val: number) => val.toFixed(1) },
      legend: { show: false },
    }),
    [computed.effectivenessByDept.labels],
  );

  const issuesDonutOptions = (labels: string[], titleLabel: string, colors: string[]): ApexOptions => ({
    ...donutBase,
    labels,
    colors,
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            total: {
              show: true,
              label: titleLabel,
              fontSize: '14px',
              fontWeight: 600,
              formatter: (w: any) => (w?.globals?.seriesTotals || []).reduce((a: number, b: number) => a + b, 0),
            },
          },
        },
      },
    },
  });

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <div className="np-page-head">
        <div>
          <h4 className="mb-1">Reports &amp; Analytics</h4>
          <p className="np-sub">Visualize your GRC data and gain insights</p>
        </div>
        <div className="np-page-actions">
          <button type="button" className="np-btn np-btn-ghost" onClick={exportToPDF}>
            <i className="ri-file-pdf-line"></i> Export PDF
          </button>
          <button type="button" className="np-btn np-btn-ghost" onClick={exportToExcel}>
            <i className="ri-file-excel-line"></i> Export Excel
          </button>
        </div>
      </div>

      <div className="np-tabs">
        <button
          type="button"
          className={`np-tab ${activeTab === 'risk' ? 'active' : ''}`}
          onClick={() => setActiveTab('risk')}
        >
          <i className="ri-alert-line me-2"></i>Risk Reports
        </button>
        <button
          type="button"
          className={`np-tab ${activeTab === 'control' ? 'active' : ''}`}
          onClick={() => setActiveTab('control')}
        >
          <i className="ri-shield-check-line me-2"></i>Control Reports
        </button>
        <button
          type="button"
          className={`np-tab ${activeTab === 'compliance' ? 'active' : ''}`}
          onClick={() => setActiveTab('compliance')}
        >
          <i className="ri-file-list-3-line me-2"></i>Compliance Reports
        </button>
        <button
          type="button"
          className={`np-tab ${activeTab === 'issues' ? 'active' : ''}`}
          onClick={() => setActiveTab('issues')}
        >
          <i className="ri-error-warning-line me-2"></i>Issues &amp; Incidents
        </button>
        <button
          type="button"
          className={`np-tab ${activeTab === 'gap' ? 'active' : ''}`}
          onClick={() => setActiveTab('gap')}
        >
          <i className="ri-focus-3-line me-2"></i>Gap Analysis
        </button>
      </div>

      {/* Risk Reports */}
      {activeTab === 'risk' && (
        <>
          <div className="np-grid np-grid-5" style={{ marginBottom: '1.25rem' }}>
            <div className="np-card">
              <div className="np-card-body np-stat">
                <span className="np-stat-icon np-tone-primary"><i className="ri-shield-line"></i></span>
                <p className="np-stat-label">Total Risks</p>
                <p className="np-stat-value np-tabular">{query.isLoading ? '-' : computed.riskStats.total}</p>
              </div>
            </div>
            <div className="np-card">
              <div className="np-card-body np-stat">
                <span className="np-stat-icon np-tone-danger"><i className="ri-alarm-warning-line"></i></span>
                <p className="np-stat-label">Critical Risks</p>
                <p className="np-stat-value np-tabular np-tone-danger">{query.isLoading ? '-' : computed.riskStats.critical}</p>
              </div>
            </div>
            <div className="np-card">
              <div className="np-card-body np-stat">
                <span className="np-stat-icon np-tone-warning"><i className="ri-arrow-up-line"></i></span>
                <p className="np-stat-label">Avg. Inherent Risk</p>
                <p className="np-stat-value np-tabular np-tone-warning">{query.isLoading ? '-' : computed.riskStats.avgInherentRisk}</p>
              </div>
            </div>
            <div className="np-card">
              <div className="np-card-body np-stat">
                <span className="np-stat-icon np-tone-info"><i className="ri-arrow-down-line"></i></span>
                <p className="np-stat-label">Avg. Residual Risk</p>
                <p className="np-stat-value np-tabular np-tone-info">{query.isLoading ? '-' : computed.riskStats.avgResidualRisk}</p>
              </div>
            </div>
            <div className="np-card">
              <div className="np-card-body np-stat">
                <span className="np-stat-icon np-tone-success"><i className="ri-checkbox-circle-line"></i></span>
                <p className="np-stat-label">Mitigated Risks</p>
                <p className="np-stat-value np-tabular np-tone-success">{query.isLoading ? '-' : computed.riskStats.mitigated}</p>
              </div>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-8 col-12">
              <Card title={<><i className="ri-fire-line text-danger me-2"></i>Risk Heat Map</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={computed.riskHeatmapSeries.every((s) => s.data.every((v: number) => v === 0))}
                  emptyText="No risk data available"
                >
                  <ReactApexChart options={riskHeatmapOptions} series={computed.riskHeatmapSeries as any} type="heatmap" height={350} />
                </ChartOrEmpty>
              </Card>
            </div>
            <div className="col-lg-4 col-12">
              <Card title={<><i className="ri-list-ordered me-2"></i>Top 10 Risks</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={computed.topRisks.scores.length === 0}
                  emptyText="No risk data available"
                >
                  <ReactApexChart
                    options={topRisksOptions}
                    series={[{ name: 'Risk Score', data: computed.topRisks.scores }]}
                    type="bar"
                    height={350}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6 col-12">
              <Card title={<><i className="ri-flow-chart me-2"></i>Risk Treatment Pipeline</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={computed.treatmentPipeline.values.every((v) => v === 0)}
                  emptyText="No risk data available"
                >
                  <ReactApexChart
                    options={pipelineOptions}
                    series={[{ name: 'Risks', data: computed.treatmentPipeline.values }]}
                    type="bar"
                    height={280}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
            <div className="col-lg-6 col-12">
              <Card title={<><i className="ri-pie-chart-2-line me-2"></i>Risks by Category</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={computed.riskByCategory.values.length === 0}
                  emptyText="No risk data available"
                >
                  <ReactApexChart
                    options={riskByCategoryOptions}
                    series={computed.riskByCategory.values}
                    type="donut"
                    height={280}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
          </div>

          <Card title={<><i className="ri-bar-chart-grouped-line me-2"></i>Risk Score by Category</>}>
            <p className="np-sub mb-3">Total risk exposure (sum of L×I scores) per category</p>
            <ChartOrEmpty
              isLoading={query.isLoading}
              isError={!!query.error}
              empty={computed.riskScoreByCategory.values.length === 0}
              emptyText="No risk data available"
            >
              <ReactApexChart
                options={riskScoreByCategoryOptions}
                series={[{ name: 'Total Risk Score', data: computed.riskScoreByCategory.values }]}
                type="bar"
                height={350}
              />
            </ChartOrEmpty>
          </Card>

          {(() => {
            const allRows = computed.gapAnalysis?.rows || [];
            const conformityRows = allRows.filter((r) => r.conformityRisk);
            if (conformityRows.length === 0) return null;
            const acceptedCount = conformityRows.filter((r) => r.riskAccepted).length;
            const expiredCount  = conformityRows.filter((r) => r.acceptanceExpired).length;
            const unmitigated   = conformityRows.filter((r) => !r.riskAccepted && !r.acceptanceExpired).length;

            const allFrameworks = Array.from(new Set(conformityRows.map((r) => r.framework).filter(Boolean))).sort();

            let filtered = conformityRows;
            if (acceptSearch.trim()) {
              const s = acceptSearch.toLowerCase();
              filtered = filtered.filter((r) =>
                r.name.toLowerCase().includes(s) ||
                r.userControlId.toLowerCase().includes(s) ||
                r.riskAcceptanceOwner.toLowerCase().includes(s)
              );
            }
            if (acceptStatusFilter !== 'all') {
              filtered = filtered.filter((r) =>
                acceptStatusFilter === 'accepted'   ? r.riskAccepted :
                acceptStatusFilter === 'expired'    ? r.acceptanceExpired :
                !r.riskAccepted && !r.acceptanceExpired
              );
            }
            if (acceptFrameworkFilter !== 'all') {
              filtered = filtered.filter((r) => r.framework === acceptFrameworkFilter);
            }

            const statusOrder = (r: typeof filtered[0]) => r.riskAccepted ? 1 : r.acceptanceExpired ? 2 : 0;
            filtered = [...filtered].sort((a, b) => {
              let cmp = 0;
              switch (acceptSortCol) {
                case 'name':      cmp = a.name.localeCompare(b.name); break;
                case 'framework': cmp = a.framework.localeCompare(b.framework); break;
                case 'status':    cmp = statusOrder(a) - statusOrder(b); break;
                case 'owner':     cmp = a.riskAcceptanceOwner.localeCompare(b.riskAcceptanceOwner); break;
                case 'on':        cmp = a.riskAcceptanceDate.localeCompare(b.riskAcceptanceDate); break;
                case 'period':    cmp = a.riskAcceptanceReviewPeriod.localeCompare(b.riskAcceptanceReviewPeriod); break;
                case 'until':     cmp = a.riskAcceptanceUntil.localeCompare(b.riskAcceptanceUntil); break;
              }
              return acceptSortDir === 'asc' ? cmp : -cmp;
            });

            const sortToggle = (col: string) => {
              if (acceptSortCol === col) setAcceptSortDir((d) => d === 'asc' ? 'desc' : 'asc');
              else { setAcceptSortCol(col); setAcceptSortDir('asc'); }
            };
            const sortIcon = (col: string) =>
              acceptSortCol !== col
                ? <i className="ri-arrows-up-down-line ms-1 np-hint" style={{ fontSize: '0.7rem', opacity: 0.5 }}></i>
                : acceptSortDir === 'asc'
                  ? <i className="ri-sort-asc ms-1" style={{ fontSize: '0.7rem' }}></i>
                  : <i className="ri-sort-desc ms-1" style={{ fontSize: '0.7rem' }}></i>;

            return (
              <Card title={<><i className="ri-shield-user-line text-warning me-2"></i>Control Risk Acceptance</>}>
                <p className="np-sub mb-3">
                  Controls with a conformity risk — shows which gaps have been formally accepted, by whom, for how long, and which remain unmitigated.
                </p>
                <div className="np-mini-stat-grid-4 mb-4">
                  <div className="np-mini-stat">
                    <div className="np-mini-stat-value np-tabular np-tone-danger">{conformityRows.length}</div>
                    <div className="np-mini-stat-label">Conformity Risks</div>
                  </div>
                  <div className="np-mini-stat">
                    <div className="np-mini-stat-value np-tabular np-tone-warning">{acceptedCount}</div>
                    <div className="np-mini-stat-label">Accepted</div>
                  </div>
                  <div className="np-mini-stat">
                    <div className="np-mini-stat-value np-tabular np-tone-neutral">{expiredCount}</div>
                    <div className="np-mini-stat-label">Expired Acceptances</div>
                  </div>
                  <div className="np-mini-stat">
                    <div className="np-mini-stat-value np-tabular np-tone-info">{unmitigated}</div>
                    <div className="np-mini-stat-label">Unmitigated</div>
                  </div>
                </div>

                {/* Filters */}
                <div className="np-toolbar mb-3">
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <div className="np-search">
                      <i className="ri-search-line"></i>
                      <input
                        type="text"
                        placeholder="Search control, ID or owner…"
                        value={acceptSearch}
                        onChange={(e) => setAcceptSearch(e.target.value)}
                      />
                      {acceptSearch && (
                        <button type="button" className="np-icon-btn" onClick={() => setAcceptSearch('')}>
                          <i className="ri-close-line"></i>
                        </button>
                      )}
                    </div>
                    <select
                      className="np-select"
                      style={{ width: 'auto' }}
                      value={acceptStatusFilter}
                      onChange={(e) => setAcceptStatusFilter(e.target.value)}
                    >
                      <option value="all">All statuses</option>
                      <option value="unmitigated">Unmitigated</option>
                      <option value="accepted">Accepted</option>
                      <option value="expired">Expired</option>
                    </select>
                    {allFrameworks.length > 1 && (
                      <select
                        className="np-select"
                        style={{ width: 'auto' }}
                        value={acceptFrameworkFilter}
                        onChange={(e) => setAcceptFrameworkFilter(e.target.value)}
                      >
                        <option value="all">All frameworks</option>
                        {allFrameworks.map((fw) => <option key={fw} value={fw}>{fw}</option>)}
                      </select>
                    )}
                    {(acceptSearch || acceptStatusFilter !== 'all' || acceptFrameworkFilter !== 'all') && (
                      <button
                        type="button"
                        className="np-btn np-btn-ghost np-btn-sm"
                        onClick={() => { setAcceptSearch(''); setAcceptStatusFilter('all'); setAcceptFrameworkFilter('all'); }}
                      >
                        <i className="ri-filter-off-line"></i>Clear
                      </button>
                    )}
                  </div>
                </div>

                <NimbusTable
                  thead={
                    <tr>
                      <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => sortToggle('name')}>Control{sortIcon('name')}</th>
                      <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => sortToggle('framework')}>Framework{sortIcon('framework')}</th>
                      <th>Conformity Issues</th>
                      <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => sortToggle('status')}>Status{sortIcon('status')}</th>
                      <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => sortToggle('owner')}>Accepted By{sortIcon('owner')}</th>
                      <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => sortToggle('on')}>Accepted On{sortIcon('on')}</th>
                      <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => sortToggle('period')}>Review Period{sortIcon('period')}</th>
                      <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => sortToggle('until')}>Accepted Until{sortIcon('until')}</th>
                    </tr>
                  }
                  isEmpty={filtered.length === 0}
                  emptyState={<div className="text-center np-sub py-3">No matching controls</div>}
                >
                  {filtered.map((row) => (
                    <tr key={row.controlId}>
                      <td>
                        <div className="np-row-title" style={{ fontSize: '0.828rem' }}>{row.name}</div>
                        {row.userControlId && <div className="np-hint">{row.userControlId}</div>}
                      </td>
                      <td><span className="np-chip np-tone-neutral">{row.framework || '—'}</span></td>
                      <td>
                        <ul className="mb-0 ps-3 np-hint">
                          {row.conformityReasons.map((r) => <li key={r}>{r}</li>)}
                        </ul>
                      </td>
                      <td>
                        {row.riskAccepted ? (
                          <span className="np-chip np-tone-warning"><i className="ri-shield-user-line"></i>Accepted</span>
                        ) : row.acceptanceExpired ? (
                          <span className="np-chip np-tone-neutral"><i className="ri-alarm-warning-line"></i>Expired</span>
                        ) : (
                          <span className="np-chip np-tone-danger"><i className="ri-shield-cross-line"></i>Unmitigated</span>
                        )}
                      </td>
                      <td className="np-tabular">{row.riskAcceptanceOwner || <span className="np-hint">—</span>}</td>
                      <td className="np-tabular">{row.riskAcceptanceDate || <span className="np-hint">—</span>}</td>
                      <td className="np-tabular">{row.riskAcceptanceReviewPeriod || <span className="np-hint">—</span>}</td>
                      <td className={`np-tabular${row.acceptanceExpired ? ' np-tone-danger' : ''}`} style={row.acceptanceExpired ? { color: 'var(--bs-danger-text-emphasis)', fontWeight: 600 } : undefined}>
                        {row.riskAcceptanceUntil || <span className="np-hint">—</span>}
                        {row.acceptanceExpired && <><br/><span style={{ fontSize: '0.7rem' }}><i className="ri-alarm-warning-line"></i> Expired</span></>}
                      </td>
                    </tr>
                  ))}
                </NimbusTable>
                {filtered.length > 0 && (
                  <div className="mt-2 np-sub text-end np-tabular">
                    Showing {filtered.length} of {conformityRows.length} controls
                  </div>
                )}
              </Card>
            );
          })()}
        </>
      )}

      {/* Control Reports */}
      {activeTab === 'control' && (
        <>
          <div className="row g-4">
            <div className="col-lg-6 col-12">
              <Card title={<><i className="ri-shield-check-line me-2"></i>Control Effectiveness</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={!computed.controlEffectiveness.percentages.some((v, idx) => v > 0 && computed.controlEffectiveness.labels[idx] !== 'Not Effective')}
                  emptyText="No effectiveness data available. Please update control effectiveness ratings."
                >
                  <ReactApexChart
                    options={controlEffectivenessOptions}
                    series={computed.controlEffectiveness.percentages}
                    type="donut"
                    height={280}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
            <div className="col-lg-6 col-12">
              <Card title={<><i className="ri-bar-chart-box-line me-2"></i>Controls by Type</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={computed.controlsByType.values.length === 0}
                  emptyText="No control data available"
                >
                  <ReactApexChart
                    options={simpleBarOptions(computed.controlsByType.labels)}
                    series={[{ name: 'Controls', data: computed.controlsByType.values }]}
                    type="bar"
                    height={280}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6 col-12">
              <Card title={<><i className="ri-progress-3-line me-2"></i>Implementation Status</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={computed.controlImplementation.values.every((v) => v === 0)}
                  emptyText="No implementation data available"
                >
                  <ReactApexChart
                    options={issuesDonutOptions(computed.controlImplementation.labels, 'Total Controls', ['#ffc107', '#17a2b8', '#28a745', '#6c757d'])}
                    series={computed.controlImplementation.values}
                    type="donut"
                    height={280}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
            <div className="col-lg-6 col-12">
              <Card title={<><i className="ri-line-chart-line me-2"></i>Maturity Distribution</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={computed.controlMaturity.values.every((v) => v === 0)}
                  emptyText="No maturity data available"
                >
                  <ReactApexChart
                    options={simpleBarOptions(computed.controlMaturity.labels)}
                    series={[{ name: 'Controls', data: computed.controlMaturity.values }]}
                    type="bar"
                    height={280}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6 col-12">
              <Card title={<><i className="ri-layout-grid-line me-2"></i>Control Categories</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={computed.controlCategory.values.length === 0}
                  emptyText="No control data available"
                >
                  <ReactApexChart
                    options={{ ...donutBase, labels: computed.controlCategory.labels, colors: ['#696cff', '#8e92ff', '#ff6384', '#36a2eb', '#ffce56', '#4bc0c0', '#9966ff'] }}
                    series={computed.controlCategory.values}
                    type="donut"
                    height={280}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
            <div className="col-lg-6 col-12">
              <Card title={<><i className="ri-test-tube-line me-2"></i>Testing Status</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={computed.controlTesting.values.every((v) => v === 0)}
                  emptyText="No testing data available"
                >
                  <ReactApexChart
                    options={{ ...donutBase, labels: computed.controlTesting.labels, colors: ['#28a745', '#ffc107', '#dc3545', '#6c757d'] }}
                    series={computed.controlTesting.values}
                    type="donut"
                    height={280}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Compliance Reports */}
      {activeTab === 'compliance' && (
        <>
          <Card title={<><i className="ri-file-list-3-line me-2"></i>Framework Coverage</>}>
            <ChartOrEmpty
              isLoading={query.isLoading}
              isError={!!query.error}
              empty={computed.frameworkCoverage.frameworks.length === 0}
              emptyText="No framework data available"
            >
              <ReactApexChart options={frameworkCoverageOptions} series={frameworkCoverageOptions.series as any} type="bar" height={350} />
            </ChartOrEmpty>
          </Card>

          <div className="row g-4">
            <div className="col-lg-6 col-12">
              <Card title={<><i className="ri-checkbox-circle-line me-2"></i>Control Implementation Alignment</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={computed.implementationAlignment.values.every((v) => v === 0)}
                  emptyText="No implementation data available"
                >
                  <ReactApexChart
                    options={implementationAlignmentOptions}
                    series={computed.implementationAlignment.values}
                    type="donut"
                    height={280}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
            <div className="col-lg-6 col-12">
              <Card title={<><i className="ri-bar-chart-fill me-2"></i>Average Control Effectiveness</>}>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="np-sub">Overall Score:</span>
                    <span className="h3 mb-0 np-tabular">{query.isLoading ? '0' : String(computed.avgEffectiveness.average || '0')}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <span className="np-sub">Assessed Controls:</span>
                    <span className="np-tabular">{query.isLoading ? '0 of 0' : `${computed.avgEffectiveness.scored || 0} of ${computed.avgEffectiveness.total || 0}`}</span>
                  </div>
                </div>

                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={!Object.values(computed.avgEffectiveness.breakdown).some((v) => v > 0)}
                  emptyText="No effectiveness data available"
                >
                  <ReactApexChart
                    options={avgEffectivenessOptions}
                    series={Object.values(computed.avgEffectiveness.breakdown)}
                    type="donut"
                    height={280}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
          </div>

          <Card title={<><i className="ri-team-line me-2"></i>Average Control Effectiveness by Department</>}>
            <ChartOrEmpty
              isLoading={query.isLoading}
              isError={!!query.error}
              empty={computed.effectivenessByDept.labels.length === 0 || computed.effectivenessByDept.values.length === 0}
              emptyText="No department effectiveness data available"
            >
              <ReactApexChart
                options={effectivenessByDeptOptions}
                series={[{ name: 'Avg Effectiveness', data: computed.effectivenessByDept.values }]}
                type="bar"
                height={400}
              />
            </ChartOrEmpty>
          </Card>
        </>
      )}

      {/* Issues & Incidents */}
      {activeTab === 'issues' && (
        <>
          <div className="row g-4">
            <div className="col-lg-6 col-12">
              <Card title={<><i className="ri-error-warning-line me-2"></i>Issue Status Distribution</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={computed.issueStatus.values.length === 0}
                  emptyText="No issue data available"
                >
                  <ReactApexChart
                    options={issuesDonutOptions(computed.issueStatus.labels, 'Total Issues', ['#dc3545', '#ffc107', '#17a2b8', '#28a745'])}
                    series={computed.issueStatus.values}
                    type="donut"
                    height={280}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
            <div className="col-lg-6 col-12">
              <Card title={<><i className="ri-alarm-warning-line me-2"></i>Incident Severity</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={computed.incidentSeverity.values.every((v) => v === 0)}
                  emptyText="No incident data available"
                >
                  <ReactApexChart
                    options={simpleBarOptions(computed.incidentSeverity.labels)}
                    series={[{ name: 'Incidents', data: computed.incidentSeverity.values }]}
                    type="bar"
                    height={280}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6 col-12">
              <Card title={<><i className="ri-star-line me-2"></i>Issue Priority Distribution</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={computed.issuePriority.values.every((v) => v === 0)}
                  emptyText="No priority data available"
                >
                  <ReactApexChart
                    options={issuesDonutOptions(computed.issuePriority.labels, 'Total Issues', ['#dc3545', '#fd7e14', '#ffc107', '#28a745'])}
                    series={computed.issuePriority.values}
                    type="donut"
                    height={280}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
            <div className="col-lg-6 col-12">
              <Card title={<><i className="ri-building-line me-2"></i>Issues by Department</>}>
                <ChartOrEmpty
                  isLoading={query.isLoading}
                  isError={!!query.error}
                  empty={computed.issueDepartment.values.length === 0}
                  emptyText="No department data available"
                >
                  <ReactApexChart
                    options={{
                      chart: { type: 'bar', height: 280, toolbar: { show: true } },
                      plotOptions: { bar: { horizontal: true, borderRadius: 8 } },
                      xaxis: { categories: computed.issueDepartment.labels },
                      dataLabels: { enabled: true },
                      colors: ['#696cff'],
                      legend: { show: false },
                    }}
                    series={[{ name: 'Issues', data: computed.issueDepartment.values }]}
                    type="bar"
                    height={280}
                  />
                </ChartOrEmpty>
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Gap Analysis */}
      {activeTab === 'gap' && (() => {
        const { rows } = computed.gapAnalysis;

        // Per-framework filter
        const availableFrameworks = Array.from(new Set(rows.map((r) => r.framework).filter(Boolean))).sort();
        const frameworkRows = gapFrameworkFilter === 'all' ? rows : rows.filter((r) => r.framework === gapFrameworkFilter);

        // Unique options for dropdown filters (derived from frameworkRows so they stay relevant)
        const deptOptions = Array.from(new Set(frameworkRows.map((r) => r.department).filter(Boolean))).sort();
        const maturityOptions = Array.from(new Set(frameworkRows.map((r) => r.currentLabel))).sort((a, b) => {
          const sa = maturityToScore(a);
          const sb = maturityToScore(b);
          if (sa === null && sb === null) return a.localeCompare(b);
          if (sa === null) return 1;
          if (sb === null) return -1;
          return sa - sb;
        });
        const statusOptions = Array.from(new Set(frameworkRows.map((r) => r.implementationStatus).filter(Boolean))).sort();

        // Recompute summary from frameworkRows
        const withCurrentFW = frameworkRows.filter((r) => r.currentScore !== null);
        const avgCurrentFW = withCurrentFW.length
          ? (withCurrentFW.reduce((s, r) => s + r.currentScore!, 0) / withCurrentFW.length).toFixed(1)
          : '—';
        const conformingCount = frameworkRows.filter((r) => !r.conformityRisk).length;
        const fwSummary = {
          total: frameworkRows.length,
          conformityRisk: frameworkRows.filter((r) => r.conformityRisk).length,
          conforming: conformingCount,
          pctConforming: frameworkRows.length ? Math.round(conformingCount / frameworkRows.length * 100) : 0,
          belowRepeatable: frameworkRows.filter((r) => r.belowRepeatable).length,
          withEvidence: frameworkRows.filter((r) => r.hasEvidence).length,
          pctWithEvidence: frameworkRows.length ? Math.round(frameworkRows.filter((r) => r.hasEvidence).length / frameworkRows.length * 100) : 0,
          withRaci: frameworkRows.filter((r) => r.hasRaciOwnership).length,
          pctWithRaci: frameworkRows.length ? Math.round(frameworkRows.filter((r) => r.hasRaciOwnership).length / frameworkRows.length * 100) : 0,
          needsRemediation: frameworkRows.filter((r) => r.needsRemediation).length,
          pctNeedsRemediation: frameworkRows.length ? Math.round(frameworkRows.filter((r) => r.needsRemediation).length / frameworkRows.length * 100) : 0,
          avgCurrentMaturity: avgCurrentFW,
        };

        // Conformity breakdown across ALL frameworks (regardless of current filter)
        const allFrameworkConformity = availableFrameworks.map((fw) => {
          const fwR = rows.filter((r) => r.framework === fw);
          const conf = fwR.filter((r) => !r.conformityRisk).length;
          const pct = fwR.length ? Math.round(conf / fwR.length * 100) : 0;
          return { fw, total: fwR.length, conforming: conf, nonConforming: fwR.length - conf, pct };
        });

        // Maturity distribution for this framework
        const fwDistribCounts = [0, 0, 0, 0, 0, 0];
        frameworkRows.forEach((r) => { fwDistribCounts[r.currentScore ?? 0] += 1; });
        const fwMaturityDistrib = { labels: MATURITY_SHORT_LABELS, values: fwDistribCounts };

        // Dept breakdown from RACI R/A for this framework
        const fwDeptCountMap: Record<string, { count: number; belowCount: number; conformityRiskCount: number }> = {};
        frameworkRows.forEach((r) => {
          const depts = r.raciDepts.length > 0 ? r.raciDepts : [r.department || 'Unassigned'];
          depts.forEach((dept) => {
            if (!fwDeptCountMap[dept]) fwDeptCountMap[dept] = { count: 0, belowCount: 0, conformityRiskCount: 0 };
            fwDeptCountMap[dept].count += 1;
            if (r.belowRepeatable) fwDeptCountMap[dept].belowCount += 1;
            if (r.conformityRisk) fwDeptCountMap[dept].conformityRiskCount += 1;
          });
        });
        const fwDeptBreakdown = Object.entries(fwDeptCountMap)
          .map(([name, { count, belowCount, conformityRiskCount }]) => ({ name, count, belowCount, conformityRiskCount }))
          .sort((a, b) => b.conformityRiskCount - a.conformityRiskCount)
          .slice(0, 8);

        const handleSort = (col: GapSortCol) => {
          if (gapSortCol === col) {
            setGapSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
          } else {
            setGapSortCol(col);
            setGapSortDir('asc');
          }
        };

        const sortIcon = (col: GapSortCol) => {
          if (gapSortCol !== col) return <i className="ri-arrow-up-down-line ms-1 np-hint" style={{ fontSize: '0.75rem', opacity: 0.5 }}></i>;
          return gapSortDir === 'asc'
            ? <i className="ri-arrow-up-s-line ms-1" style={{ fontSize: '0.85rem' }}></i>
            : <i className="ri-arrow-down-s-line ms-1" style={{ fontSize: '0.85rem' }}></i>;
        };

        const filteredRows = frameworkRows
          .filter((r) => !gapShowBelowOnly || r.belowRepeatable)
          .filter((r) => !gapShowConformityOnly || r.conformityRisk)
          .filter((r) => gapDeptFilter === 'all' || r.department === gapDeptFilter)
          .filter((r) => gapMaturityFilter === 'all' || r.currentLabel === gapMaturityFilter)
          .filter((r) => gapStatusFilter === 'all' || r.implementationStatus === gapStatusFilter)
          .filter((r) => {
            if (!gapSearch) return true;
            const q = gapSearch.toLowerCase();
            return (
              r.name.toLowerCase().includes(q) ||
              r.responsible.toLowerCase().includes(q) ||
              r.department.toLowerCase().includes(q) ||
              r.userControlId.toLowerCase().includes(q)
            );
          })
          .sort((a, b) => {
            const dir = gapSortDir === 'asc' ? 1 : -1;
            switch (gapSortCol) {
              case 'name': {
                const aid = a.userControlId || a.name;
                const bid = b.userControlId || b.name;
                return dir * aid.localeCompare(bid, undefined, { numeric: true, sensitivity: 'base' });
              }
              case 'department': return dir * a.department.localeCompare(b.department);
              case 'responsible': return dir * a.responsible.localeCompare(b.responsible);
              case 'currentScore': {
                const av = a.currentScore ?? -1, bv = b.currentScore ?? -1;
                return dir * (av - bv);
              }
              case 'gap': {
                const av = a.gap ?? 999, bv = b.gap ?? 999;
                return dir * (av - bv);
              }
              case 'implementationStatus': return dir * a.implementationStatus.localeCompare(b.implementationStatus);
              case 'evidenceCount': return dir * (a.evidenceCount - b.evidenceCount);
              case 'conformityRisk': return dir * ((a.conformityRisk ? 1 : 0) - (b.conformityRisk ? 1 : 0));
              default: return 0;
            }
          });

        const maturityBadge = (score: number | null, label: string) => {
          if (score === null || label === '') return <span className="np-hint">Not set</span>;
          const colour = score === 0 ? 'danger' : score === 1 ? 'warning' : score === 2 ? 'info' : score === 3 ? 'primary' : 'success';
          const short = MATURITY_SHORT_LABELS[score] || label;
          return <span className={`np-chip np-tone-${colour}`} title={label}>{score} – {short}</span>;
        };

        const gapBadge = (gap: number | null) => {
          if (gap === null) return <span className="np-hint">—</span>;
          if (gap === 0) return <span className="np-chip np-tone-success">On target</span>;
          if (gap < 0) return <span className="np-chip np-tone-success">{Math.abs(gap)} ahead</span>;
          return (
            <span className="np-chip np-tone-danger" title={`Needs to improve ${gap} level${gap !== 1 ? 's' : ''}`}>
              +{gap} level{gap !== 1 ? 's' : ''}
            </span>
          );
        };

        const statusBadge = (s: string) => {
          if (!s) return <span className="np-hint">Not set</span>;
          const sl = s.toLowerCase();
          const colour = sl.includes('complete') || sl.includes('implement') ? 'success' : sl.includes('progress') ? 'primary' : sl.includes('hold') ? 'neutral' : 'warning';
          return <span className={`np-chip np-tone-${colour}`} style={{ textTransform: 'capitalize' }}>{s}</span>;
        };

        return (
          <>
            {/* Context callout */}
            <div className="np-banner np-tone-primary mb-4">
              <i className="ri-information-line"></i>
              <div>
                <strong>Control Gap Analysis</strong>
                <p className="mb-0 mt-1">
                  Controls are rated on a 0–5 maturity scale (Unassessed → Optimized). Controls below{' '}
                  <strong>Repeatable (2)</strong> are not yet consistently practised and represent the highest-priority
                  gaps in your ISMS programme. The <em>Conformance</em> column flags in-scope controls that fail any of:{' '}
                  not fully implemented, maturity below Repeatable, no evidence linked, no RACI ownership, overdue test/review date,
                  evidence overdue for review, or evidence never reviewed. Hover the <strong>Risk</strong> badge to see the exact reasons.
                </p>
              </div>
            </div>

            {/* Framework tabs */}
            {availableFrameworks.length > 1 && (
              <div className="np-pill-toggle-group mb-4">
                <button
                  type="button"
                  className={`np-pill-toggle np-tone-primary${gapFrameworkFilter === 'all' ? ' active' : ''}`}
                  onClick={() => setGapFrameworkFilter('all')}
                >
                  All Frameworks
                  <span className="np-chip np-tone-neutral np-tabular" style={{ marginLeft: 6 }}>{rows.length}</span>
                </button>
                {availableFrameworks.map((fw) => (
                  <button
                    key={fw}
                    type="button"
                    className={`np-pill-toggle np-tone-primary${gapFrameworkFilter === fw ? ' active' : ''}`}
                    onClick={() => setGapFrameworkFilter(fw)}
                  >
                    {fw}
                    <span className="np-chip np-tone-neutral np-tabular" style={{ marginLeft: 6 }}>{rows.filter((r) => r.framework === fw).length}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Conformity overview */}
            <div className="row g-3 mb-3">
              {/* Overall conformity */}
              <div className="col-lg-4 col-md-6">
                <div className={`np-card np-card-accent-${!query.isLoading && fwSummary.pctConforming >= 80 ? 'success' : !query.isLoading && fwSummary.pctConforming >= 50 ? 'warning' : 'danger'}`} style={{ height: '100%' }}>
                  <div className="np-card-body">
                    <p className="np-sub text-uppercase fw-semibold mb-1" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Overall Conformity</p>
                    <div className="d-flex align-items-baseline gap-2 mb-2">
                      <h2 className={`mb-0 np-tabular np-stat-value np-tone-${!query.isLoading && fwSummary.pctConforming >= 80 ? 'success' : !query.isLoading && fwSummary.pctConforming >= 50 ? 'warning' : 'danger'}`}>
                        {query.isLoading ? '—' : `${fwSummary.pctConforming}%`}
                      </h2>
                      <span className="np-hint np-tabular">{query.isLoading ? '' : `${fwSummary.conforming} / ${fwSummary.total} controls`}</span>
                    </div>
                    <div className="np-progress mb-2">
                      <div
                        className={`np-fill np-tone-${fwSummary.pctConforming >= 80 ? 'success' : fwSummary.pctConforming >= 50 ? 'warning' : 'danger'}`}
                        style={{ width: `${fwSummary.pctConforming}%` }}
                      />
                    </div>
                    <div className="d-flex justify-content-between np-tabular" style={{ fontSize: '0.8125rem' }}>
                      <span style={{ color: 'var(--bs-success-text-emphasis)' }}><i className="ri-checkbox-circle-line me-1"></i>{query.isLoading ? '—' : fwSummary.conforming} conforming</span>
                      <span style={{ color: 'var(--bs-danger-text-emphasis)' }}><i className="ri-close-circle-line me-1"></i>{query.isLoading ? '—' : fwSummary.conformityRisk} non-conforming</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stage 1 — Documentation & Governance */}
              <div className="col-lg-4 col-md-6">
                <div className={`np-card np-card-accent-${stage1Pct >= 80 ? 'success' : stage1Pct >= 50 ? 'warning' : 'danger'}`} style={{ height: '100%' }}>
                  <div className="np-card-body">
                    <p className="np-sub text-uppercase fw-semibold mb-1" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Stage 1 — Documentation &amp; Governance</p>
                    <div className="d-flex align-items-baseline gap-2 mb-2">
                      <h2 className={`mb-0 np-tabular np-stat-value np-tone-${stage1Pct >= 80 ? 'success' : stage1Pct >= 50 ? 'warning' : 'danger'}`}>
                        {`${stage1Pct}%`}
                      </h2>
                      <span className="np-hint np-tabular">{stage1Done} / {stage1Steps.length} steps</span>
                    </div>
                    <div className="np-progress mb-2">
                      <div
                        className={`np-fill np-tone-${stage1Pct >= 80 ? 'success' : stage1Pct >= 50 ? 'warning' : 'danger'}`}
                        style={{ width: `${stage1Pct}%` }}
                      />
                    </div>
                    <p className="np-hint mb-2">ISMS Journey steps before the Stage 1 milestone</p>
                    <div style={{ borderTop: '1px solid var(--np-border)', paddingTop: '0.5rem' }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="np-hint" style={{ fontSize: '0.72rem' }}>Controls with owner (RACI)</span>
                        <span
                          className="fw-semibold np-tabular"
                          style={{ fontSize: '0.72rem', color: fwSummary.pctWithRaci >= 80 ? 'var(--bs-success-text-emphasis)' : fwSummary.pctWithRaci >= 50 ? 'var(--bs-warning-text-emphasis)' : 'var(--bs-danger-text-emphasis)' }}
                        >
                          {query.isLoading ? '—' : `${fwSummary.pctWithRaci}%`}
                        </span>
                      </div>
                      <div className="np-progress" style={{ height: 4 }}>
                        <div
                          className={`np-fill np-tone-${fwSummary.pctWithRaci >= 80 ? 'success' : fwSummary.pctWithRaci >= 50 ? 'warning' : 'danger'}`}
                          style={{ width: `${fwSummary.pctWithRaci}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stage 2 — Implementation & Operations */}
              <div className="col-lg-4 col-md-6">
                <div className={`np-card np-card-accent-${stage2Pct >= 80 ? 'success' : stage2Pct >= 50 ? 'warning' : 'danger'}`} style={{ height: '100%' }}>
                  <div className="np-card-body">
                    <p className="np-sub text-uppercase fw-semibold mb-1" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Stage 2 — Implementation &amp; Operations</p>
                    <div className="d-flex align-items-baseline gap-2 mb-2">
                      <h2 className={`mb-0 np-tabular np-stat-value np-tone-${stage2Pct >= 80 ? 'success' : stage2Pct >= 50 ? 'warning' : 'danger'}`}>
                        {`${stage2Pct}%`}
                      </h2>
                      <span className="np-hint np-tabular">{stage2Done} / {stage2Steps.length} steps</span>
                    </div>
                    <div className="np-progress mb-2">
                      <div
                        className={`np-fill np-tone-${stage2Pct >= 80 ? 'success' : stage2Pct >= 50 ? 'warning' : 'danger'}`}
                        style={{ width: `${stage2Pct}%` }}
                      />
                    </div>
                    <p className="np-hint mb-2">ISMS Journey steps after the Stage 1 milestone</p>
                    <div style={{ borderTop: '1px solid var(--np-border)', paddingTop: '0.5rem' }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="np-hint" style={{ fontSize: '0.72rem' }}>Controls with evidence linked</span>
                        <span
                          className="fw-semibold np-tabular"
                          style={{ fontSize: '0.72rem', color: fwSummary.pctWithEvidence >= 80 ? 'var(--bs-success-text-emphasis)' : fwSummary.pctWithEvidence >= 50 ? 'var(--bs-warning-text-emphasis)' : 'var(--bs-danger-text-emphasis)' }}
                        >
                          {query.isLoading ? '—' : `${fwSummary.pctWithEvidence}%`}
                        </span>
                      </div>
                      <div className="np-progress" style={{ height: 4 }}>
                        <div
                          className={`np-fill np-tone-${fwSummary.pctWithEvidence >= 80 ? 'success' : fwSummary.pctWithEvidence >= 50 ? 'warning' : 'danger'}`}
                          style={{ width: `${fwSummary.pctWithEvidence}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Conformity by framework (only when multiple frameworks) */}
            {allFrameworkConformity.length > 1 && (
              <div className="np-card mb-3">
                <div className="np-card-body">
                  <p className="np-sub text-uppercase fw-semibold mb-3" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Conformity by Framework</p>
                  <div className="d-flex flex-column gap-3">
                    {allFrameworkConformity.map(({ fw, total, conforming, nonConforming, pct }) => (
                      <div key={fw}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="fw-medium" style={{ fontSize: '0.828rem' }}>{fw}</span>
                          <span className="np-hint np-tabular">{pct}% &nbsp;<span style={{ color: 'var(--bs-success-text-emphasis)' }}>{conforming}✓</span> &nbsp;<span style={{ color: 'var(--bs-danger-text-emphasis)' }}>{nonConforming}✗</span> &nbsp;/ {total}</span>
                        </div>
                        <div className="np-progress" style={{ height: 6 }}>
                          <div
                            className={`np-fill np-tone-${pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'danger'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Summary tiles */}
            <div className="np-mini-stat-grid-6 mb-3">
              <div className="np-mini-stat">
                <div className="np-mini-stat-value np-tabular">{query.isLoading ? '—' : fwSummary.total}</div>
                <div className="np-mini-stat-label">Total Controls</div>
              </div>
              <div className="np-mini-stat">
                <div className="np-mini-stat-value np-tabular np-tone-danger">{query.isLoading ? '—' : fwSummary.conformityRisk}</div>
                <div className="np-mini-stat-label">Conformity Risks</div>
              </div>
              <div className="np-mini-stat">
                <div className="np-mini-stat-value np-tabular np-tone-danger">{query.isLoading ? '—' : fwSummary.belowRepeatable}</div>
                <div className="np-mini-stat-label">Below Repeatable</div>
              </div>
              <div className="np-mini-stat">
                <div className={`np-mini-stat-value np-tabular ${!query.isLoading && fwSummary.pctWithEvidence < 50 ? 'np-tone-warning' : 'np-tone-success'}`}>
                  {query.isLoading ? '—' : `${fwSummary.pctWithEvidence}%`}
                </div>
                <div className="np-mini-stat-label">Controls with Evidence</div>
              </div>
              <div className="np-mini-stat">
                <div className={`np-mini-stat-value np-tabular ${!query.isLoading && fwSummary.pctWithRaci < 80 ? 'np-tone-warning' : 'np-tone-success'}`}>
                  {query.isLoading ? '—' : `${fwSummary.pctWithRaci}%`}
                </div>
                <div className="np-mini-stat-label">Controls with RACI</div>
              </div>
              <div className="np-mini-stat">
                <div className="np-mini-stat-value np-tabular">{query.isLoading ? '—' : fwSummary.avgCurrentMaturity}</div>
                <div className="np-mini-stat-label">Avg. Maturity</div>
              </div>
            </div>

            {/* Charts row */}
            <div className="row g-3 mb-3">
              {/* Maturity distribution */}
              <div className="col-lg-7">
                <Card title={<><i className="ri-bar-chart-box-line me-2"></i>Maturity Distribution</>}>
                  <ChartOrEmpty
                    isLoading={query.isLoading}
                    isError={!!query.error}
                    empty={fwMaturityDistrib.values.every((v) => v === 0)}
                    emptyText="No maturity data. Open controls and set Current Maturity."
                  >
                    <ReactApexChart
                      options={{
                        chart: { type: 'bar', height: 220, toolbar: { show: false } },
                        plotOptions: { bar: { borderRadius: 5, distributed: true, columnWidth: '60%' } },
                        colors: ['#dc3545', '#fd7e14', '#ffc107', '#20c997', '#198754', '#0d6efd'],
                        xaxis: { categories: fwMaturityDistrib.labels },
                        yaxis: { title: { text: 'Controls' }, tickAmount: Math.max(...fwMaturityDistrib.values) > 0 ? undefined : 1 },
                        dataLabels: { enabled: true },
                        legend: { show: false },
                        tooltip: { y: { formatter: (v: number) => `${v} control${v !== 1 ? 's' : ''}` } },
                      }}
                      series={[{ name: 'Controls', data: fwMaturityDistrib.values }]}
                      type="bar"
                      height={220}
                    />
                  </ChartOrEmpty>
                </Card>
              </div>

              {/* Conformity risk by department */}
              <div className="col-lg-5">
                <Card title={<><i className="ri-building-line me-2"></i>Conformity by Department</>}>
                  <ChartOrEmpty
                    isLoading={query.isLoading}
                    isError={!!query.error}
                    empty={fwDeptBreakdown.length === 0}
                    emptyText="No department data. RACI R/A assignments are used to attribute controls to departments."
                  >
                    <ReactApexChart
                      options={{
                        chart: { type: 'bar', height: 220, toolbar: { show: false }, stacked: true },
                        plotOptions: { bar: { borderRadius: 0, horizontal: true, barHeight: '60%' } },
                        colors: ['#dc3545', '#198754'],
                        xaxis: { categories: fwDeptBreakdown.map((d) => d.name) },
                        yaxis: { labels: { style: { fontSize: '12px' } } },
                        dataLabels: { enabled: false },
                        legend: { show: true, position: 'top' as const, fontSize: '11px' },
                        tooltip: { y: { formatter: (v: number) => `${v} control${v !== 1 ? 's' : ''}` } },
                      }}
                      series={[
                        { name: 'At Risk', data: fwDeptBreakdown.map((d) => d.conformityRiskCount) },
                        { name: 'Clean', data: fwDeptBreakdown.map((d) => d.count - d.conformityRiskCount) },
                      ]}
                      type="bar"
                      height={220}
                    />
                  </ChartOrEmpty>
                </Card>
              </div>
            </div>

            {/* Controls by department distribution */}
            <div className="row g-3 mb-4">
              <div className="col-12">
                <Card title={<><i className="ri-bar-chart-horizontal-line me-2"></i>Controls by Department</>}>
                  <ChartOrEmpty
                    isLoading={query.isLoading}
                    isError={!!query.error}
                    empty={fwDeptBreakdown.length === 0}
                    emptyText="No department data. Assign departments to controls."
                  >
                    <ReactApexChart
                      options={{
                        chart: { type: 'bar', height: 240, toolbar: { show: false } },
                        plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: '55%', distributed: true } },
                        colors: ['#0d6efd'],
                        xaxis: { categories: fwDeptBreakdown.map((d) => d.name), title: { text: 'Number of Controls' } },
                        yaxis: { labels: { style: { fontSize: '12px' } } },
                        dataLabels: { enabled: true, formatter: (v: number) => `${v}` },
                        legend: { show: false },
                        tooltip: { y: { formatter: (v: number) => `${v} control${v !== 1 ? 's' : ''}` } },
                      }}
                      series={[{ name: 'Controls', data: fwDeptBreakdown.map((d) => d.count) }]}
                      type="bar"
                      height={240}
                    />
                  </ChartOrEmpty>
                </Card>
              </div>
            </div>

            {/* Controls table */}
            <Card title={<><i className="ri-table-line me-2"></i>All Controls — Maturity Gap Detail</>}>
              <div className="d-flex flex-wrap gap-3 align-items-center mb-3">
                <div className="d-flex align-items-center gap-2">
                  <label className="np-switch mb-0">
                    <input
                      type="checkbox"
                      id="gapFilterSwitch"
                      checked={gapShowBelowOnly}
                      onChange={(e) => setGapShowBelowOnly(e.target.checked)}
                    />
                    <span className="np-switch-track"></span>
                    <span className="np-switch-thumb"></span>
                  </label>
                  <label htmlFor="gapFilterSwitch" className="mb-0">
                    Show below Repeatable only
                    {gapShowBelowOnly && <span className="np-chip np-tone-danger ms-2 np-tabular">{fwSummary.belowRepeatable}</span>}
                  </label>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <label className="np-switch mb-0">
                    <input
                      type="checkbox"
                      id="gapConformitySwitch"
                      checked={gapShowConformityOnly}
                      onChange={(e) => setGapShowConformityOnly(e.target.checked)}
                    />
                    <span className="np-switch-track"></span>
                    <span className="np-switch-thumb"></span>
                  </label>
                  <label htmlFor="gapConformitySwitch" className="mb-0">
                    Conformity risks only
                    {gapShowConformityOnly && <span className="np-chip np-tone-danger ms-2 np-tabular">{fwSummary.conformityRisk}</span>}
                  </label>
                </div>
                <div className="np-search ms-auto" style={{ minWidth: 240 }}>
                  <i className="ri-search-line"></i>
                  <input
                    type="text"
                    placeholder="Search control, owner, department…"
                    value={gapSearch}
                    onChange={(e) => setGapSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <select
                  className="np-select"
                  style={{ width: 'auto', minWidth: 150, maxWidth: 190 }}
                  value={gapDeptFilter}
                  onChange={(e) => setGapDeptFilter(e.target.value)}
                >
                  <option value="all">All Departments</option>
                  {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <select
                  className="np-select"
                  style={{ width: 'auto', minWidth: 150, maxWidth: 210 }}
                  value={gapFrameworkFilter}
                  onChange={(e) => setGapFrameworkFilter(e.target.value)}
                >
                  <option value="all">All Frameworks</option>
                  {availableFrameworks.map((fw) => <option key={fw} value={fw}>{fw}</option>)}
                </select>
                <select
                  className="np-select"
                  style={{ width: 'auto', minWidth: 150, maxWidth: 220 }}
                  value={gapMaturityFilter}
                  onChange={(e) => setGapMaturityFilter(e.target.value)}
                >
                  <option value="all">All Maturity Levels</option>
                  {maturityOptions.map((m) => <option key={m || '__blank__'} value={m}>{m || '(Not set)'}</option>)}
                </select>
                <select
                  className="np-select"
                  style={{ width: 'auto', minWidth: 150, maxWidth: 210 }}
                  value={gapStatusFilter}
                  onChange={(e) => setGapStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {(gapDeptFilter !== 'all' || gapFrameworkFilter !== 'all' || gapMaturityFilter !== 'all' || gapStatusFilter !== 'all') && (
                  <button
                    type="button"
                    className="np-btn np-btn-ghost np-btn-sm"
                    onClick={() => { setGapDeptFilter('all'); setGapFrameworkFilter('all'); setGapMaturityFilter('all'); setGapStatusFilter('all'); }}
                  >
                    <i className="ri-filter-off-line"></i>Clear filters
                  </button>
                )}
              </div>

              {query.isLoading ? (
                <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div></div>
              ) : filteredRows.length === 0 ? (
                <div className="text-center np-sub py-5">
                  <i className="ri-shield-check-line fs-1 d-block mb-2"></i>
                  {rows.length === 0
                    ? 'No controls found. Add controls and set Current Maturity and Target Maturity to populate this report.'
                    : 'No controls match the current filter.'}
                </div>
              ) : (
                <NimbusTable thead={<tr>
                        <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Control{sortIcon('name')}</th>
                        <th style={{ width: '140px', cursor: 'pointer' }} onClick={() => handleSort('department')}>Department{sortIcon('department')}</th>
                        <th style={{ width: '140px', cursor: 'pointer' }} onClick={() => handleSort('responsible')}>Responsible{sortIcon('responsible')}</th>
                        <th style={{ width: '150px', cursor: 'pointer' }} onClick={() => handleSort('currentScore')}>Maturity{sortIcon('currentScore')}</th>
                        <th style={{ width: '120px', cursor: 'pointer' }} onClick={() => handleSort('gap')}>Gap{sortIcon('gap')}</th>
                        <th style={{ width: '100px', cursor: 'pointer' }} onClick={() => handleSort('evidenceCount')}>Evidence{sortIcon('evidenceCount')}</th>
                        <th style={{ width: '140px', cursor: 'pointer' }} onClick={() => handleSort('implementationStatus')}>Status{sortIcon('implementationStatus')}</th>
                        <th style={{ width: '110px', cursor: 'pointer' }} onClick={() => handleSort('conformityRisk')}>Conformance{sortIcon('conformityRisk')}</th>
                      </tr>}>
                      {filteredRows.map((row) => (
                        <tr key={row.controlId} style={row.belowRepeatable ? { borderLeft: '3px solid var(--bs-danger)' } : undefined}>
                          <td>
                            <div className="fw-medium">
                              {row.controlId && row.matrixId ? (
                                <Link
                                  to={`/controls/${encodeURIComponent(row.controlId)}?matrixId=${encodeURIComponent(row.matrixId)}`}
                                  className="text-body"
                                  style={{ textDecoration: 'none' }}
                                >
                                  {row.name}
                                  <i className="ri-external-link-line ms-1 np-hint" style={{ fontSize: '0.75rem', verticalAlign: 'middle' }}></i>
                                </Link>
                              ) : row.name}
                            </div>
                            {row.userControlId && (
                              <div className="np-hint">{row.userControlId}</div>
                            )}
                          </td>
                          <td className="np-hint">{row.department || '—'}</td>
                          <td>
                            {row.responsible === 'Unassigned'
                              ? <span className="np-hint">Unassigned</span>
                              : <span className="np-chip np-tone-primary">{row.responsible}</span>}
                          </td>
                          <td>{maturityBadge(row.currentScore, row.currentLabel)}</td>
                          <td>{gapBadge(row.gap)}</td>
                          <td className="text-center">
                            {row.hasEvidence
                              ? <span className="np-chip np-tone-success np-tabular" title={`${row.evidenceCount} linked document${row.evidenceCount !== 1 ? 's' : ''}`}><i className="ri-file-check-line"></i>{row.evidenceCount}</span>
                              : <span className="np-chip np-tone-neutral np-tabular" title="No linked documents"><i className="ri-file-line"></i>0</span>}
                          </td>
                          <td>{statusBadge(row.implementationStatus)}</td>
                          <td className="text-center">
                            <div className="d-flex flex-column align-items-center gap-1">
                              {row.conformityRisk ? (
                                <span
                                  className="np-chip np-tone-danger"
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="left"
                                  data-bs-html="true"
                                  title={row.conformityChecked.map((c) =>
                                    c.passed
                                      ? `<span style="text-decoration:line-through;opacity:0.55">&#10003; ${c.label}</span>`
                                      : `<span style="color:var(--bs-danger-border-subtle)">&#10007; ${c.label}</span>`
                                  ).join('<br/>')}
                                  style={{ cursor: 'default' }}
                                >
                                  <i className="ri-shield-cross-line"></i>Risk
                                </span>
                              ) : row.isInScope ? (
                                <span
                                  className="np-chip np-tone-success"
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="left"
                                  data-bs-html="true"
                                  title={row.conformityChecked.map((c) =>
                                    `<span style="opacity:0.85">&#10003; ${c.label}</span>`
                                  ).join('<br/>')}
                                  style={{ cursor: 'default' }}
                                ><i className="ri-shield-check-line"></i>OK</span>
                              ) : (
                                <span className="np-hint">—</span>
                              )}
                              {row.conformityRisk && row.riskAccepted && (
                                <span
                                  className="np-chip np-tone-warning"
                                  title={`Accepted until ${row.riskAcceptanceUntil}${row.riskAcceptanceOwner ? ' by ' + row.riskAcceptanceOwner : ''}. Reason: ${row.riskAcceptanceReason.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()}`}
                                  style={{ cursor: 'default' }}
                                >
                                  <i className="ri-shield-user-line"></i>Accepted
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                </NimbusTable>
              )}
              {filteredRows.length > 0 && (
                <div className="mt-2 np-sub text-end np-tabular">
                  Showing {filteredRows.length} of {rows.length} controls
                  {fwSummary.needsRemediation > 0 && (
                    <span className="ms-3" style={{ color: 'var(--bs-warning-text-emphasis)' }}>
                      <i className="ri-tools-line me-1"></i>{fwSummary.needsRemediation} need remediation ({fwSummary.pctNeedsRemediation}%)
                    </span>
                  )}
                </div>
              )}
            </Card>
          </>
        );
      })()}

    </div>
  );
};
